import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ReplaySubject } from 'rxjs';

import { IActivity, IActivityList } from '@azavista/activity-lib';
import {
    IMenuItem, IField, IDependency, ILanguageItem, IObjectWithId, IEventSettings, IMergetagPosition, IAttributeTranslation, ProductType,
    ICartItemProduct,
    IProductVisibility,
    IEventPageResponse,
    IValidationResult
} from './shared/interfaces';
import { AppService } from './app.service';
import { UtilsService, delay, getMapByGetter } from './shared/utils.service';
import { MergetagPrefix, MergetagSuffix, VideoProviderName } from './shared/enums';
import {
    ActivitiesCartDisplayType, AttributeName, CountdownValueType, DocumentFieldCallbackDataClickedItemName,
    ElementId, IActivityWithDependencyId, IApplyDependenciesResult, ICreateVideoPlayerResult,
    IDocumentFieldCallbackData, IIdWithValue, IImageFieldCallbackData,
    IVideoPlayer, QueryParamsInput, TagName
} from './interfaces';
import { gTagPath } from './shared/javascript-integration-snippets';
import { ClassName, ContentLoadStateName, FieldAttributeTranslationValue, SearchParamName, SpecialPageId, TRANSLATION_MAP, TranslationKey } from './enums';
import { MomentService } from './shared/moment.service';
import { AdvancedSearchLibService } from './shared/advanced-search-lib.service';
import { fieldType, Workflow } from '@azavista/advanced-search';
import { ShoppingCartResultUI } from '@azavista/payments';
import { SharedService } from './shared/shared.service';
import { AppLoggerService, LogLevel } from './shared/app-logger.service';
import { EventSettings } from '@azavista/advanced-search/lib/search-types';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';

// TODO: Create classes for different video providers and encapsulate logic there
declare const Vimeo: any;
declare const YT: any;

@Injectable({
    providedIn: 'root'
})
export class AppDomService {
    dynamicEl: HTMLDivElement;

    private id = 0;
    private youtubeIframeAPIReady = false;
    private countdownSectionsTimerHandle = 0;
    private countdownTimerInterval = 1000;
    private hrefImagesClickCallback: (href: string, target?: string) => void;
    // private pageThemeLoadPromisesMap: { [key: string]: { loadStateName: ContentLoadStateName, promise: Promise<void> } } = {};
    private youtubeIframeAPIReadySubject = new ReplaySubject<void>(1);
    eventSettings: IEventSettings;
    private readonly dummyObjectId = '000000000000000000000000';

    constructor(
        private appSvc: AppService,
        private appLoggerSvc: AppLoggerService,
        private sharedSvc: SharedService,
        private utilsSvc: UtilsService,
        private titleService: Title,
        private momentSvc: MomentService,
        private translateSvc: TranslateService,
        private readonly advancedSearchLibSvc: AdvancedSearchLibService
    ) {
    }

    submitRevereForm(token: string, actionUrl: string): void {
        const revereFormEl = document.createElement('form');
        revereFormEl.action = actionUrl;
        revereFormEl.method = 'post';
        const tokenEl = document.createElement('input');
        tokenEl.value = token;
        revereFormEl.appendChild(tokenEl);
        document.appendChild(revereFormEl);
        revereFormEl.submit();
        revereFormEl.remove();
    }

    setEventSettings(eventSettings: IEventSettings): void {
        this.eventSettings = eventSettings;
    }

    setFavIcon(href: string): void {
        const relValue = 'icon';
        let existing = document.head.querySelector<HTMLLinkElement>(this.getAttributeWithValueSelector(AttributeName.rel, relValue));
        if (!existing) {
            existing = document.createElement('link');
            existing.rel = relValue;
            existing.href = href;
            document.head.appendChild(existing);
        } else {
            existing.href = href;
        }
    }

    getNewId(): string {
        this.id++;
        return '' + this.id;
    }

    isElementRequired(el: HTMLElement): boolean {
        const attr = el.getAttribute(AttributeName.azaInputRequired);
        return (!!attr || attr === '');
    }

    attachToProfileButtons(logInClicked: () => void, profileClicked: () => void): void {
        const profileLogInLinks = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaProfileLoginLink));
        profileLogInLinks.forEach(x => {
            x.onclick = ev => {
                ev.preventDefault();
                logInClicked();
            };
        });
        const profileProfileLinks = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaProfileProfileLink));
        profileProfileLinks.forEach(x => {
            x.onclick = ev => {
                ev.preventDefault();
                profileClicked();
            };
        });
    }

    changeProfileButtonsVisibility(showLoginButton: boolean, showProfileButton: boolean): void {
        const displayNoneClassName = 'display-none';
        const profileLogInLinks = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaProfileLoginLink));
        profileLogInLinks.forEach(x => {
            if (showLoginButton) {
                this.removeClass(x, displayNoneClassName);
            }
            else {
                this.addClass(x, displayNoneClassName);
            }
        });
        const profileProfileLinks = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaProfileProfileLink));
        profileProfileLinks.forEach(x => {
            if (showProfileButton) {
                this.removeClass(x, displayNoneClassName);
            }
            else {
                this.addClass(x, displayNoneClassName);
            }
        });
    }

    setHrefImagesClickCallback(callback: ((href: string, target?: string) => void)): void {
        this.hrefImagesClickCallback = callback;
    }

    attachToHrefImages(): void {
        const hrefImageEls = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaImageHref));
        hrefImageEls.forEach(el => {
            const parentAnchorEl = el.parentElement as HTMLAnchorElement;
            const hasAnchorParentWithHref = parentAnchorEl && parentAnchorEl.tagName === 'A' && parentAnchorEl.href;
            if (!hasAnchorParentWithHref) {
                // Does not have anchor parent or it does not have href - attach to image click if it has href
                const imageHref = el.getAttribute(AttributeName.azaImageHref);
                if (this.hrefImagesClickCallback && imageHref && imageHref.trim()) {
                    const hrefTarget = el.getAttribute(AttributeName.azaImageHrefTarget);
                    el.onclick = ev => this.hrefImagesClickCallback(imageHref, hrefTarget);
                }
            }
        });
    }

    stopCountdownSections(): void {
        window.clearInterval(this.countdownSectionsTimerHandle);
    }

    startCountdownSections(eventStartDate: string): void {
        this.stopCountdownSections();
        const azaCountdownSections = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaCountdown));
        if (azaCountdownSections.length === 0) {
            return;
        }
        const allDaysEls: Element[] = [];
        const allHoursEls: Element[] = [];
        const allMinutesEls: Element[] = [];
        const allSecondsEls: Element[] = [];
        azaCountdownSections.forEach(el => {
            el.querySelectorAll(this.getAttributeWithValueSelector(AttributeName.azaCountdownValueType, CountdownValueType.days))
                .forEach(x => allDaysEls.push(x));
            el.querySelectorAll(this.getAttributeWithValueSelector(AttributeName.azaCountdownValueType, CountdownValueType.hours))
                .forEach(x => allHoursEls.push(x));
            el.querySelectorAll(this.getAttributeWithValueSelector(AttributeName.azaCountdownValueType, CountdownValueType.minutes))
                .forEach(x => allMinutesEls.push(x));
            el.querySelectorAll(this.getAttributeWithValueSelector(AttributeName.azaCountdownValueType, CountdownValueType.seconds))
                .forEach(x => allSecondsEls.push(x));
        });
        const targetDate = new Date(eventStartDate);
        this.countdownSectionsTimerHandle = window.setInterval(() => {
            const timeLeft = this.utilsSvc.calculateTimeLeft(targetDate);
            this.setElementsInnerText(allDaysEls, timeLeft.days);
            this.setElementsInnerText(allHoursEls, timeLeft.hours);
            this.setElementsInnerText(allMinutesEls, timeLeft.minutes);
            this.setElementsInnerText(allSecondsEls, timeLeft.seconds);
        }, this.countdownTimerInterval);
    }

    setElementsInnerText(els: Element[] | NodeListOf<Element>, value: string | number): void {
        els.forEach((x: HTMLElement) => x.innerText = '' + value);
    }

    replaceGoogleMapElements(): void {
        const azaGoogleMapsEls = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaGoogleMaps));
        azaGoogleMapsEls.forEach(el => {
            const parent = el.parentElement;
            const embedCode = el.getAttribute(AttributeName.azaGoogleMaps);
            el.remove();
            if (embedCode) {
                const lowercasedEmbedCode = embedCode.toLowerCase().trim();
                if (lowercasedEmbedCode.indexOf('<iframe') === 0) {
                    // Google maps HTML embed code must start with "<iframe" - put only such code
                    // TODO: What if malicious planner puts malicious code like onload="javascript:..." ?
                    parent.innerHTML += embedCode;
                }
            }
        });
    }

    renderRatingFields(parentEl?: HTMLElement): void {
        parentEl ||= this.getDynamicContentContainerElement();
        const ratingFieldEls = parentEl.querySelectorAll(this.getAttributeSelector(AttributeName.azaRatingField));
        ratingFieldEls.forEach(el => {
            const inputEl = el.querySelector(this.getAttributeSelector(AttributeName.azaRatingInputField)) as HTMLInputElement;
            const valuesContainerEl = el.querySelector(this.getAttributeSelector(AttributeName.azaRatingFieldValuesContainer));
            const valuesEls = valuesContainerEl.querySelectorAll(
                this.getAttributeSelector(AttributeName.azaRatingFieldValue)
            ) as NodeListOf<HTMLElement>;
            this.setRatingSelectedElement(valuesEls, +inputEl.value);
            if (!inputEl.hasAttribute(AttributeName.disabled)) {
                valuesEls.forEach(valueEl => {
                    valueEl.onclick = clickEv => this.processRatingValueClicked(valueEl);
                });
            }
        });
    }

    setRatingSelectedElement(valueElements: NodeListOf<Element>, selectedValue: number): void {
        valueElements.forEach(el => {
            const value = +el.getAttribute(AttributeName.azaRatingFieldValue);
            const notIncludedAttr = el.getAttribute(AttributeName.azaRatingFieldNotIncludedItem);
            const includedAttr = el.getAttribute(AttributeName.azaRatingFieldIncludedItem);
            if (value <= selectedValue) {
                // This is included value - Show filled variant
                if (notIncludedAttr !== null) {
                    // We are at not included (not filled) element - hide it
                    this.addClass(el, ClassName.azaRatingNotVisibleItem);
                    this.removeClass(el, ClassName.azaRatingVisibleItem);
                } else if (includedAttr !== null) {
                    // We are at included (filled) element - show it
                    this.addClass(el, ClassName.azaRatingVisibleItem);
                    this.removeClass(el, ClassName.azaRatingNotVisibleItem);
                }
            } else {
                // This is not included value - show not filled variant
                if (notIncludedAttr !== null) {
                    // We are at not included (not filled) element - show it
                    this.addClass(el, ClassName.azaRatingVisibleItem);
                    this.removeClass(el, ClassName.azaRatingNotVisibleItem);
                } else if (includedAttr !== null) {
                    // We are at included (filled) element - hide it
                    this.addClass(el, ClassName.azaRatingNotVisibleItem);
                    this.removeClass(el, ClassName.azaRatingVisibleItem);
                }
            }
        });
    }

    private processRatingValueClicked(valueEl: HTMLElement): void {
        const value = +valueEl.getAttribute(AttributeName.azaRatingFieldValue);
        const valuesContainerEl = valueEl.parentElement;
        const valuesEls = valuesContainerEl.querySelectorAll(
            this.getAttributeSelector(AttributeName.azaRatingFieldValue)
        ) as NodeListOf<HTMLElement>;
        this.setRatingSelectedElement(valuesEls, value);
        const inputEl = valuesContainerEl.parentElement.querySelector(
            this.getAttributeSelector(AttributeName.azaRatingInputField)
        ) as HTMLInputElement;
        inputEl.value = '' + value;
        this.triggerInputEvent(inputEl);
    }


    private triggerInputEvent(el: HTMLElement): boolean {
        return el.dispatchEvent(new InputEvent('input'));
    }

    renderOpinionScaleFields(parentEl?: HTMLElement): void {
        parentEl ||= this.getDynamicContentContainerElement();
        const opinionScaleFieldEls = parentEl.querySelectorAll(this.getAttributeSelector(AttributeName.azaOpinionScaleField));
        opinionScaleFieldEls.forEach(el => {
            const inputEl = el.querySelector(this.getAttributeSelector(AttributeName.azaOpinionScaleInputField)) as HTMLInputElement;
            const valuesContainerEl = el.querySelector(this.getAttributeSelector(AttributeName.azaOpinionScaleFieldValuesContainer));
            const valuesEls = valuesContainerEl.querySelectorAll(
                this.getAttributeSelector(AttributeName.azaOpinionScaleFieldValue)
            ) as NodeListOf<HTMLElement>;
            this.setOpinionScaleSelectedElement(valuesEls, +inputEl.value);
            if (!inputEl.hasAttribute(AttributeName.disabled)) {
                valuesEls.forEach(valueEl => {
                    valueEl.onclick = clickEv => this.processOpinionScaleValueClicked(valueEl, inputEl);
                });
            }
        });
    }

    setOpinionScaleSelectedElement(valueElements: NodeListOf<Element>, selectedValue: number): void {
        valueElements.forEach(el => {
            const value = +el.getAttribute(AttributeName.azaOpinionScaleFieldValue);
            if (value !== selectedValue) {
                this.removeClass(el, ClassName.azaOpinionScaleSelectedValue);
            } else {
                this.addClass(el, ClassName.azaOpinionScaleSelectedValue);
            }
        });
    }

    processOpinionScaleValueClicked(valueEl: HTMLElement, inputEl: HTMLInputElement): void {
        const value = +valueEl.getAttribute(AttributeName.azaOpinionScaleFieldValue);
        const valuesContainerEl = valueEl.parentElement;
        const valuesEls = valuesContainerEl.querySelectorAll(
            this.getAttributeSelector(AttributeName.azaOpinionScaleFieldValue)
        ) as NodeListOf<HTMLElement>;
        this.setOpinionScaleSelectedElement(valuesEls, value);
        // const inputEl = valuesContainerEl.parentElement.querySelector(
        //     this.getAttributeSelector(AttributeName.opinionScaleInputField)
        // ) as HTMLInputElement;
        inputEl.value = '' + value;
        // Trigger input event so the rest of the app knows the input field was changed
        this.triggerInputEvent(inputEl);
    }

    setLockedFields(fields: IField[], eventParticipant: IObjectWithId, parentEl?: HTMLElement): void {
        const lockedAttributeSelector = this.getAttributeSelector(AttributeName.azaInputLockValue);
        const lockedEls = parentEl?.querySelectorAll(lockedAttributeSelector) || this.getDynamicContainerChildrenElements(lockedAttributeSelector);
        lockedEls.forEach(el => {
            const disablableElements = ['input', 'select', 'textarea'];
            type types = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
            for (const elName of disablableElements) {
                const disablableEls = el.querySelectorAll(elName);
                disablableEls.forEach(x => (x as types).disabled = true);
            }
            // Image fields are special - we should detach onclick of specific child
            const imageFieldSelectFileEl = el.querySelector(
                this.getAttributeSelector(AttributeName.azaImageFieldControlSelectFile)
            ) as HTMLElement;
            if (imageFieldSelectFileEl) {
                imageFieldSelectFileEl.onclick = null;
                imageFieldSelectFileEl.remove();
            }
            const imageFieldRemoveFileEl = el.querySelector(
                this.getAttributeSelector(AttributeName.azaImageFieldControlRemoveFile)
            ) as HTMLElement;
            if (imageFieldRemoveFileEl) {
                imageFieldRemoveFileEl.onclick = null;
                imageFieldRemoveFileEl.remove();
            }

            // Document fields should hide their "Upload a new document", the separator and the "X" button
            const docEl = el.querySelector(this.getAttributeSelector(AttributeName.azaDocumentField));
            if (docEl) {
                const fieldId = docEl.getAttribute(AttributeName.fieldId);
                const field = fields.find(x => x.id === fieldId);
                const hasAttachedDocument = !!(eventParticipant[fieldId] || eventParticipant[field?.name]);
                const uploadLinkEl = el.querySelector(this.getAttributeSelector(AttributeName.azaDocumentFieldUploadLink)) as HTMLElement;
                if (uploadLinkEl) {
                    uploadLinkEl.onclick = null;
                    if (!hasAttachedDocument) {
                        uploadLinkEl.innerText = this.appSvc.translate(TranslationKey.noDocument);
                        uploadLinkEl.classList.add(ClassName.azaLockedDocumentFieldNoDocumentUploaded);
                    } else {
                        this.hideElementWithCss(uploadLinkEl);
                    }
                }
                const separatorEl = el.querySelector(this.getAttributeSelector(AttributeName.azaDocumentFieldDownloadUploadSeparator));
                if (separatorEl) {
                    this.hideElementWithCss(separatorEl);
                }
                const removeDocEl = el.querySelector(this.getAttributeSelector(AttributeName.azaDocumentFieldControlRemoveFile)) as HTMLElement;
                if (removeDocEl) {
                    removeDocEl.onclick = null;
                    this.hideElementWithCss(removeDocEl);
                }

            }
        });
    }

    setImageFieldImageSource(fieldId: string, src: any): void {
        const imageEls = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaImageField));
        for (let i = 0; i < imageEls.length; i++) {
            const el = imageEls.item(i);
            const fldId = el.getAttribute(AttributeName.fieldId);
            if (fldId === fieldId) {
                const imageFieldImgElementEl = el.querySelector(
                    this.getAttributeSelector(AttributeName.azaImageFieldImgElement)
                ) as HTMLImageElement;
                imageFieldImgElementEl.src = src;
                break;
            }
        }
    }

    /**
     * Apply the value for field that represents the ticket-type field. This field is used in the hacked-version of multi-registration.
     *
     * https://az2019.azavista.com/product-management/frontend/azavista3.0/-/issues/3888
     */
    setTicketMultiRegistrationFields(participantFields: IField[], cartItems: ICartItemProduct[], productsVisibilities: IProductVisibility[]) {
        const cartItemMap = getMapByGetter(cartItems, (item) => item.product.id);
        const productsVisibilitiesMap = getMapByGetter(productsVisibilities, item => item.productId);
        const selectedProducts = cartItems
            .filter(({ product, cart_quantity }) => product.type === ProductType.ticket && !productsVisibilitiesMap[product.id]?.hidden && cart_quantity > 0 && cartItemMap[product.id])
            .map(({ product, cart_quantity }) => ({
                ...product,
                ...cartItemMap[product.id],
            }))
            .sort();
        this.sharedSvc.sortByString(selectedProducts, p => p.name);
        const ticketTypeValues: string[] = selectedProducts.reduce((result, product) => {
            return result.concat(new Array(product.cart_quantity).fill(product.name));
        }, [] as string[]);
        const ticketTypePrefix = 'ticket_type_mr_';
        const ticketTypeCrmPrefix = 'ticket_type_crm_mr_';
        participantFields
            .filter(({ name }) => name.startsWith(ticketTypePrefix) || name.startsWith(ticketTypeCrmPrefix))
            .forEach((field) => {
                const ticketFieldEl = this.getDynamicContainerChildrenElements(`[field-id="${field.id}"]`)[0] as HTMLInputElement;
                if (ticketFieldEl) {
                    const ticketIndex = parseInt(
                        field.name
                            .replace(ticketTypePrefix, '')
                            .replace(ticketTypeCrmPrefix, '')
                    );
                    ticketFieldEl.disabled = true;
                    ticketFieldEl.value = ticketTypeValues[ticketIndex - 1];
                }
            });
        const multiRegCountField = participantFields.find(field => field.name === 'multiregistration_count');
        if (multiRegCountField) {
            const multiRegCountElement = this.getDynamicContainerChildrenElements(`[field-id="${multiRegCountField.id}"]`)[0] as HTMLInputElement;
            if (multiRegCountElement) {
                multiRegCountElement.disabled = true;
                multiRegCountElement.value = `${ticketTypeValues.length}`;
            }
        }
    }

    attachToImageFields(
        fields: IField[], callback: (data: IImageFieldCallbackData) => void, removeImageCallback: (data: IImageFieldCallbackData) => void
    ): void {
        const imageEls = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaImageField));
        imageEls.forEach(el => {
            const fieldId = el.getAttribute(AttributeName.fieldId);
            const imageFieldImgElementEl = el.querySelector(
                this.getAttributeSelector(AttributeName.azaImageFieldImgElement)
            ) as HTMLImageElement;
            // The src of the image fields contain standard data:image/svg+xml - we must put the correct src here depending on the image id
            imageFieldImgElementEl.src = '';
            const isChildOfReadOnlyParent = !!imageFieldImgElementEl.closest(this.getAttributeSelector(AttributeName.azaReadOnlyField));
            const isRequired = !!el.parentElement?.parentElement?.hasAttribute(AttributeName.azaInputRequired);
            const controlSelectFileEl = el.querySelector(
                this.getAttributeSelector(AttributeName.azaImageFieldControlSelectFile)
            ) as HTMLElement;
            // The controlRemoveFileEl could be null for web pages created with older website builder versions
            // because they don't have remove file element
            let controlRemoveFileEl = el.querySelector(
                this.getAttributeSelector(AttributeName.azaImageFieldControlRemoveFile)
            ) as HTMLElement;
            if (isRequired) {
                controlRemoveFileEl?.remove();
            }
            if (isChildOfReadOnlyParent) {
                controlRemoveFileEl?.remove();
                controlSelectFileEl?.remove();
            } else {
                if (!controlSelectFileEl.hasAttribute(AttributeName.disabled)) {
                    controlSelectFileEl.onclick = ev => callback({
                        fieldId: fieldId,
                        event: ev
                    });
                }
                if (controlRemoveFileEl && !controlRemoveFileEl.hasAttribute(AttributeName.disabled)) {
                    controlRemoveFileEl.onclick = ev => {
                        imageFieldImgElementEl.src = '';
                        removeImageCallback({ fieldId: fieldId, event: ev });
                    };
                }
            }
        });
    }

    createDeleteElementForDocumentField(azaDocumentFieldLinksContainer: HTMLElement): HTMLDivElement {
        const htmlText = `
        <div aza-document-field-control-remove-file class="document-field-control-remove-file cursor-pointer">
            <span class="document-field-control-remove-file">
                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px">
                    <path d="M0 0h24v24H0V0z" fill="none" />
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
                </svg>
            </span>
        </div>`;
        const div = document.createElement('div');
        div.innerHTML = htmlText;
        // Assuming the HTML that needs to be added has single root element
        const result = div.children[0];
        return result as HTMLDivElement;
    }

    createResetElementForDocumentField(azaDocumentFieldLinksContainer: HTMLElement): HTMLDivElement {
        const htmlText = `
        <div aza-document-field-control-reset-file class="document-field-control-reset-file cursor-pointer">
            <span class="document-field-control-reset-file">
                <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" height="24px" width="24px" version="1.1" viewBox="0 0 489.533 489.533">
                    <g>
                        <path d="M268.175,488.161c98.2-11,176.9-89.5,188.1-187.7c14.7-128.4-85.1-237.7-210.2-239.1v-57.6c0-3.2-4-4.9-6.7-2.9   l-118.6,87.1c-2,1.5-2,4.4,0,5.9l118.6,87.1c2.7,2,6.7,0.2,6.7-2.9v-57.5c87.9,1.4,158.3,76.2,152.3,165.6   c-5.1,76.9-67.8,139.3-144.7,144.2c-81.5,5.2-150.8-53-163.2-130c-2.3-14.3-14.8-24.7-29.2-24.7c-17.9,0-31.9,15.9-29.1,33.6   C49.575,418.961,150.875,501.261,268.175,488.161z"/>
                    </g>
                </svg>
            </span>
        </div>`;
        const div = document.createElement('div');
        div.innerHTML = htmlText;
        // Assuming the HTML that needs to be added has single root element
        const result = div.children[0];
        return result as HTMLDivElement;
    }

    attachToDocumentFields(fields: IField[], eventParticipant: IObjectWithId, callback: (data: IDocumentFieldCallbackData) => void): void {
        const documentEls = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaDocumentField));

        documentEls.forEach(el => {
            const docEl = el;
            const fieldId = docEl.getAttribute(AttributeName.fieldId);
            const field = fields.find(x => x.id === fieldId);
            const hasAttachedDocument = !!(eventParticipant[fieldId] || eventParticipant[field?.name]);
            const inputEl = docEl.querySelector(this.getAttributeSelector(AttributeName.azaDocumentFieldInputField)) as HTMLInputElement;
            const isReadOnly = inputEl?.hasAttribute(AttributeName.azaReadOnlyInputField);
            const azaDocumentFieldLinksContainer: HTMLDivElement = docEl.querySelector(this.getAttributeSelector(AttributeName.azaDocumentFieldLinksContainer));
            let azaDocumentFieldControlRemoveFileEl: HTMLDivElement = azaDocumentFieldLinksContainer?.querySelector(this.getAttributeSelector(AttributeName.azaDocumentFieldControlRemoveFile));
            let azaDocumentFieldControlResetFileEl: HTMLDivElement = azaDocumentFieldLinksContainer?.querySelector(this.getAttributeSelector(AttributeName.azaDocumentFieldControlResetFile));
            const azaDocumentFieldDownloadLinkEl: HTMLSpanElement = docEl.querySelector(this.getAttributeSelector(AttributeName.azaDocumentFieldDownloadLink));
            const azaDocumentFieldDownloadUploadSeparatorEl: HTMLSpanElement = docEl.querySelector(this.getAttributeSelector(AttributeName.azaDocumentFieldDownloadUploadSeparator));
            const azaDocumentFieldAttachButtonEl: HTMLDivElement = docEl.querySelector(this.getAttributeSelector(AttributeName.azaDocumentFieldAttachButton));
            const uploadNewDocumentLinkEl: HTMLAnchorElement = docEl.querySelector(this.getAttributeSelector(AttributeName.azaDocumentFieldUploadLink));
            let documentUploadedEl = docEl.querySelector(this.getAttributeSelector(AttributeName.azaDocumentUploadedText));
            if (!hasAttachedDocument) {
                this.hideElementWithCss(azaDocumentFieldDownloadLinkEl);
                this.hideElementWithCss(azaDocumentFieldDownloadUploadSeparatorEl);
            } else {
                // Has attachment - attach to download link
                if (azaDocumentFieldDownloadLinkEl) {
                    azaDocumentFieldDownloadLinkEl.onclick = ev => {
                        ev.preventDefault();
                        ev.stopPropagation();
                        const callbackData: IDocumentFieldCallbackData = {
                            fieldId: fieldId, clickedItem: DocumentFieldCallbackDataClickedItemName.downloadLinkContainer, event: ev
                        };
                        callback(callbackData);
                    };
                }
            }
            if (isReadOnly) {
                // Document fields in readonly forms should not show elements for uploading documents
                this.hideElementWithCss(azaDocumentFieldDownloadUploadSeparatorEl);
                this.hideElementWithCss(azaDocumentFieldAttachButtonEl);
                this.hideElementWithCss(uploadNewDocumentLinkEl);
                this.hideElementWithCss(azaDocumentFieldControlRemoveFileEl);
            } else {
                // Not readonly - attach to events
                if (inputEl) {
                    if (uploadNewDocumentLinkEl) {
                        uploadNewDocumentLinkEl.onclick = ev => inputEl.click();
                    }
                    if (azaDocumentFieldAttachButtonEl) {
                        azaDocumentFieldAttachButtonEl.onclick = ev => inputEl.click();
                    }
                    // All document input fields have disabled attribute (because the builder is setting them so the user cannot click to select file)
                    // Remove disabled attribute of non read-only document fields
                    // If disabled attribute is not removed, inputEl.click() will not work and select file dialog will not be shown
                    // even if executed by clicking on other elements that cause file selection
                    inputEl.removeAttribute(AttributeName.disabled);
                    inputEl.onchange = ev => {
                        const callbackData: IDocumentFieldCallbackData = {
                            fieldId: fieldId, clickedItem: DocumentFieldCallbackDataClickedItemName.inputField, event: ev
                        };
                        if (azaDocumentFieldDownloadLinkEl) {
                            // Hide current "Download" link and show "Document uploaded" text instead
                            this.hideElementWithCss(azaDocumentFieldDownloadLinkEl);
                            if (!documentUploadedEl) {
                                documentUploadedEl = document.createElement('span');
                                documentUploadedEl.setAttribute(AttributeName.azaDocumentUploadedText, '');
                                documentUploadedEl.classList.add(AttributeName.azaDocumentUploadedText);
                                // TODO: We can use the file name if we change the document field to show it on user selection and when data is loaded from the server
                                const documentUploadedTextNodeElText = this.appSvc.translate(TRANSLATION_MAP.DOCUMENT_SELECTED);
                                const documentUploadedTextNodeEl = document.createTextNode(documentUploadedTextNodeElText);
                                documentUploadedEl.appendChild(documentUploadedTextNodeEl);
                                azaDocumentFieldDownloadLinkEl.after(documentUploadedEl);
                            }
                            this.showElementWithCss(documentUploadedEl);
                            this.showElementWithCss(azaDocumentFieldDownloadUploadSeparatorEl);
                        }
                        if (azaDocumentFieldControlRemoveFileEl) {
                            this.hideElementWithCss(azaDocumentFieldControlRemoveFileEl);
                        }
                        showDocumentResetFieldElement();
                        callback(callbackData);
                    };
                }
                // Add remove element if not found
                if (azaDocumentFieldLinksContainer) {
                    if (!azaDocumentFieldControlRemoveFileEl) {
                        azaDocumentFieldControlRemoveFileEl = this.createDeleteElementForDocumentField(azaDocumentFieldLinksContainer);
                        azaDocumentFieldLinksContainer.appendChild(azaDocumentFieldControlRemoveFileEl);
                    }
                    azaDocumentFieldControlRemoveFileEl.onclick = ev => {
                        this.hideElementWithCss(azaDocumentFieldDownloadLinkEl);
                        this.hideElementWithCss(azaDocumentFieldDownloadUploadSeparatorEl);
                        this.hideElementWithCss(documentUploadedEl);
                        ev.preventDefault();
                        ev.stopPropagation();
                        const callbackData: IDocumentFieldCallbackData = {
                            fieldId: fieldId, clickedItem: DocumentFieldCallbackDataClickedItemName.deleteElement, event: ev
                        };
                        callback(callbackData);
                        showDocumentResetFieldElement();
                        this.hideElementWithCss(azaDocumentFieldControlRemoveFileEl);
                        if (inputEl) {
                            // Removes the selected file
                            // This is needed to allow the user to select the same file which was selected before its removal
                            // If the value is not set to empty string, the user will not be able to select the same file
                            inputEl.value = '';
                        }
                    };
                    azaDocumentFieldLinksContainer.appendChild(azaDocumentFieldControlRemoveFileEl);
                    if (!eventParticipant[fieldId]) {
                        this.hideElementWithCss(azaDocumentFieldControlRemoveFileEl);
                    }
                }
                const showDocumentResetFieldElement = () => {
                    if (!azaDocumentFieldControlResetFileEl) {
                        azaDocumentFieldControlResetFileEl ||= this.createResetElementForDocumentField(azaDocumentFieldLinksContainer);
                        azaDocumentFieldLinksContainer.appendChild(azaDocumentFieldControlResetFileEl);
                        azaDocumentFieldControlResetFileEl.onclick = ev => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            const callbackData: IDocumentFieldCallbackData = {
                                fieldId: fieldId, clickedItem: DocumentFieldCallbackDataClickedItemName.reset, event: ev
                            };
                            callback(callbackData);
                            if (inputEl) {
                                // Removes the selected file
                                // This is needed to allow the user to select the same file which was selected before its removal
                                // If the value is not set to empty string, the user will not be able to select the same file
                                inputEl.value = '';
                            }
                            [azaDocumentFieldDownloadLinkEl, azaDocumentFieldDownloadUploadSeparatorEl, azaDocumentFieldControlRemoveFileEl].forEach(el => {
                                if (eventParticipant[fieldId]) {
                                    this.showElementWithCss(el);
                                } else {
                                    this.hideElementWithCss(el);
                                }
                            });
                            this.hideElementWithCss(documentUploadedEl);
                            this.hideElementWithCss(azaDocumentFieldControlResetFileEl);
                        };
                    }
                    this.showElementWithCss(azaDocumentFieldControlResetFileEl);
                };
            }
        });
    }

    showDocumentUploadProgress(fieldId: string, progress: number): void {
        const documentEls = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaDocumentField));
        for (let i = 0; i < documentEls.length; i++) {
            const docEl = documentEls.item(i);
            if (docEl.getAttribute(AttributeName.fieldId) === fieldId) {
                const progressEl = docEl.querySelector(this.getAttributeSelector(AttributeName.azaDocumentFieldUploadProgressText));
                if (progressEl) {
                    progressEl.innerHTML = `${progress} %`;
                }
                break;
            }
        }
    }

    showDocumentDownloadLink(fieldId: string): void {
        const documentEls = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaDocumentField));
        for (let i = 0; i < documentEls.length; i++) {
            const docEl = documentEls.item(i);
            if (docEl.getAttribute(AttributeName.fieldId) === fieldId) {
                const downloadEl = docEl.querySelector(this.getAttributeSelector(AttributeName.azaDocumentFieldDownloadLink)) as HTMLElement;
                downloadEl.style.display = '';
                break;
            }
        }
    }

    replaceVideoElements(): void {
        const videoEls = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaVideoPlaceholder));
        videoEls.forEach(async el => {
            const parent = el.parentElement;
            const embedCode = el.getAttribute(AttributeName.azaVideoEmbedCode);
            const videoDisableSubmitUntilEndAttributeValue = el.getAttribute(AttributeName.azaVideoDisableSubmitUntilEnd);
            const disableSubmitUntilEnd = videoDisableSubmitUntilEndAttributeValue === 'true'
                || videoDisableSubmitUntilEndAttributeValue === '';
            el.remove();
            const videoProviderName = this.getVideoProviderName(embedCode);
            if (videoProviderName !== VideoProviderName.unknown) {
                try {
                    await this.createVideo(videoProviderName, parent, embedCode, disableSubmitUntilEnd);
                } catch (err) {
                    this.showVideoError(parent, embedCode);
                }
            } else {
                this.showVideoError(parent, embedCode);
            }
        });
    }

    async createVideo(
        providerName: VideoProviderName, parentEl: HTMLElement, embedCode: string, disableSubmitUntilEnd?: boolean
    ): Promise<void> {
        let createPlayerRes: ICreateVideoPlayerResult;
        if (providerName === VideoProviderName.vimeo) {
            createPlayerRes = await this.createVimeoVideo(parentEl, embedCode);
        } else if (providerName === VideoProviderName.youtube) {
            createPlayerRes = await this.createYoutubeVideo(parentEl, embedCode);
        } else if (providerName === VideoProviderName.wistia) {
            createPlayerRes = await this.createWistiaVideo(parentEl, embedCode);
        }

        if (disableSubmitUntilEnd) {
            const submitButtonEls = this.getSubmitButtons();
            submitButtonEls.forEach(el => {
                el.disabled = true;
            });
            createPlayerRes.player.onEnd = () => {
                submitButtonEls.forEach(el => el.disabled = false);
            };
        }
    }

    showVideoError(parentEl: HTMLElement, embedCode: string): void {
        const errTextEl = document.createElement('p');
        this.addClass(errTextEl, ClassName.regAppErrorContent);
        errTextEl.innerText = `Cannot create video from specified embed code: '${embedCode}'`;
        parentEl.appendChild(errTextEl);
    }

    createYoutubeVideo(parentEl: HTMLElement, embedCode: string): Promise<ICreateVideoPlayerResult> {
        return new Promise(async (resolve, reject) => {
            await this.loadScript('https://www.youtube.com/iframe_api');
            this.createYouTubeIframeAPIReadyCallback();
            if (!this.youtubeIframeAPIReady) {
                const subscription = this.youtubeIframeAPIReadySubject.subscribe({
                    next: () => {
                        subscription.unsubscribe();
                        const result = this.createYoutubePlayerForElement(parentEl, embedCode);
                        resolve(result);
                    }
                });
            } else {
                const result = this.createYoutubePlayerForElement(parentEl, embedCode);
                resolve(result);
            }
        });
    }

    createVimeoVideo(parentEl: HTMLElement, embedCode: string): Promise<ICreateVideoPlayerResult> {
        return new Promise(async (resolve, reject) => {
            await this.loadScript('https://player.vimeo.com/api/player.js');
            const result = this.createVimeoPlayerForElement(parentEl, embedCode);
            resolve(result);
        });
    }

    createWistiaVideo(parentEl: HTMLElement, embedCode: string): Promise<ICreateVideoPlayerResult> {
        return new Promise(async (resolve, reject) => {
            let mediaId: string;
            const embedMediasText = 'wistia.com/embed/medias/';
            const embedMediasIndex = embedCode.indexOf(embedMediasText);
            if (embedMediasIndex > 0) {
                // We found inline snippet - try to get the video code
                // TODO: We are assuming media id is 10 chars long
                mediaId = embedCode.substr(embedMediasIndex + embedMediasText.length, 10);
            } else {
                // Try to find iframe snippet
                const iframeText = '/embed/iframe/';
                const iframeIndex = embedCode.indexOf(iframeText);
                if (iframeIndex > 0) {
                    // TODO: We are assuming that media id is 10 chars long - otherwise it will be wrong to search for next /, ? or "
                    mediaId = embedCode.substr(iframeIndex + iframeText.length, 10);
                }
            }
            if (embedMediasIndex > 0 && mediaId) {
                // Wistia is embedded inline - it requires this script to be loaded
                await this.loadScript(`https://fast.wistia.com/embed/medias/${mediaId}.jsonp`);
            }
            await this.loadScript('https://fast.wistia.com/assets/external/E-v1.js');
            const result = this.createWistiaPlayerForElement(parentEl, embedCode);
            resolve(result);
        });
    }

    loadScript(src: string, additionalAttributes?: IIdWithValue[]): Promise<void> {
        return new Promise((resolve, reject) => {
            let scriptEl: HTMLScriptElement = document.head.querySelector(`script[src='${src}']`);
            if (!scriptEl) {
                scriptEl = document.createElement('script');
                if (additionalAttributes?.length > 0) {
                    additionalAttributes.forEach(x => scriptEl.setAttribute(x.id, x.value));
                }
                scriptEl.setAttribute(AttributeName.contentLoadState, ContentLoadStateName.loading);
                scriptEl.src = src;
                scriptEl.addEventListener('load', loadEv => {
                    scriptEl.setAttribute(AttributeName.contentLoadState, ContentLoadStateName.loaded);
                    resolve();
                });
                scriptEl.onerror = errEv => {
                    scriptEl.setAttribute(AttributeName.contentLoadState, ContentLoadStateName.error);
                    reject(errEv);
                };
                document.head.appendChild(scriptEl);
            } else {
                // Script is already created - react depending on its loading / loaded state
                const scriptContentLoadState = scriptEl.getAttribute(AttributeName.contentLoadState);
                if (scriptContentLoadState === ContentLoadStateName.loaded) {
                    // We already have loaded script (probably because it was loaded for previous video)
                    // Just create player for the parent element
                    resolve();
                } else if (scriptContentLoadState === ContentLoadStateName.loading) {
                    // The script is still loading (for another video)
                    // Attach to load event so we can react for current video
                    scriptEl.addEventListener('load', loadEv => {
                        resolve();
                    });
                } else if (scriptContentLoadState === ContentLoadStateName.error) {
                    reject('Script faild to load');
                }
            }
        });
    }

    loadJavascriptIntegration(id: string, script: string, executeOnPageLoad = false, waitForScriptId?: string): Promise<void> {
        return new Promise((resolve, reject) => {
            let scriptEl: HTMLScriptElement = document.head.querySelector(`script[aza-script-id='${id}']`);
            if (scriptEl && executeOnPageLoad) {
                document.head.removeChild(scriptEl);
                scriptEl = undefined;
            }
            if (!scriptEl) {
                scriptEl = document.createElement('script');
                scriptEl.type = 'text/javascript';
                scriptEl.innerHTML = this.sanitizeScript(script);
                scriptEl.setAttribute('aza-script-id', id);
                scriptEl.onerror = errEv => reject(errEv);
                document.head.appendChild(scriptEl);
                if (waitForScriptId) {
                    const otherScript = document.getElementById(waitForScriptId);
                    if (otherScript) {
                        otherScript.addEventListener('load', loadEv => resolve());
                    } else {
                        resolve();
                    }
                } else {
                    resolve();
                }
            } else {
                resolve();
            }
        });
    }

    insertGoogleAnalyticsMetaTag(gaId: string): void {
        const scriptElem = document.createElement('script');
        scriptElem.src = gTagPath.replace('G-ID-PLACEHOLDER', gaId);
        scriptElem.async = true;
        document.querySelector('head').appendChild(scriptElem);
    }

    pushDataToGoogleTagManager(data: any): void {
        const dataLayer = (window as any).dataLayer;
        if (!dataLayer || !data) {
            return;
        }
        dataLayer.push(data);
    }

    setPageTitle(title: string) {
        this.titleService.setTitle(title || 'Azavista registration');
    }

    sanitizeScript(script: string): string {
        return script.replace(/<\/?script>/g, '');
    }

    createYoutubePlayerForElement(parentEl: HTMLElement, embedCode: string): ICreateVideoPlayerResult {
        const youtubeWrapperEl = document.createElement('div');
        youtubeWrapperEl.innerHTML = embedCode;
        parentEl.appendChild(youtubeWrapperEl);
        const youtubeIframeEl = youtubeWrapperEl.querySelector('iframe');
        youtubeIframeEl.id = 'youtube-video-' + this.getNewId();
        const youtubePlayer = new YT.Player(youtubeIframeEl.id, {
            events: {
                onReady: () => this.appLoggerSvc.log(LogLevel.log, 'YT player ready'),
                onStateChange: (videoStateChangeEvent: any) => {
                    if (videoStateChangeEvent.data === YT.PlayerState.ENDED) {
                        // Video ended
                        if (player.onEnd) {
                            player.onEnd();
                        }
                    }
                }
            }
        });
        const player: IVideoPlayer = {};
        const result: ICreateVideoPlayerResult = {
            wrapperElement: youtubeWrapperEl,
            player: player
        };
        return result;
    }

    createWistiaPlayerForElement(parentEl: HTMLElement, embedCode: string): ICreateVideoPlayerResult {
        const wistiaWrapperEl = document.createElement('div');
        wistiaWrapperEl.innerHTML = embedCode;
        (window as any)._wq = (window as any)._wq || [];
        (window as any)._wq.push({
            // TODO: only standard embed (inline) code works with this, iframe based embed code does not
            id: '_all', // mediaId,
            onReady: (video: any) => {
                video.bind('end', () => {
                    // Video ended
                    if (player.onEnd) {
                        player.onEnd();
                    }
                });
            }
        });
        parentEl.appendChild(wistiaWrapperEl);
        const player: IVideoPlayer = {};
        const result: ICreateVideoPlayerResult = {
            wrapperElement: wistiaWrapperEl,
            player: player
        };
        return result;
    }

    createVimeoPlayerForElement(parentEl: HTMLElement, embedCode: string): ICreateVideoPlayerResult {
        const vimeoWrapperEl = document.createElement('div');
        vimeoWrapperEl.innerHTML = embedCode;
        parentEl.appendChild(vimeoWrapperEl);
        const vimeoIframeEl = vimeoWrapperEl.querySelector('iframe');
        // Trim the src attribute, otherwise Vimeo.Player will throw error because it does not allow trailing spaces
        vimeoIframeEl.src = vimeoIframeEl.src.trim();
        const vimeoPlayer = new Vimeo.Player(vimeoIframeEl);
        const player: IVideoPlayer = {};
        vimeoPlayer.on('ended', (endedEv: any) => {
            if (player.onEnd) {
                player.onEnd();
            }
        });
        const result: ICreateVideoPlayerResult = {
            wrapperElement: vimeoWrapperEl,
            player: player
        };
        return result;
    }

    getVideoProviderName(videoEmbedCode: string): VideoProviderName {
        if (!videoEmbedCode) {
            return VideoProviderName.unknown;
        }
        const lowercased = videoEmbedCode.trim().toLowerCase();
        if (lowercased.indexOf('https://player.vimeo.com') > 0) {
            return VideoProviderName.vimeo;
        } else if (lowercased.indexOf('https://www.youtube.com/embed') > 0) {
            return VideoProviderName.youtube;
        } else if (lowercased.indexOf('wistia.net/') > 0 || lowercased.indexOf('wistia.com/') > 0) {
            return VideoProviderName.wistia;
        }
        return VideoProviderName.unknown;
    }

    replaceRecaptchaElements(): void {
        const recaptchaEls = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaRecaptcha));
        recaptchaEls.forEach(el => {
            this.clearElementInnerText(el);
            // TODO: We need to have server secret generated for the domain in order to use Google reCaptcha
            //       Server support is also needed to verify the value that the client sends along with the form values
        });
    }

    createAndPostPaymentForm(postUrl: string, formObject: Record<string, string>): void {
        const formEl = document.createElement('form');
        formEl.action = postUrl;
        formEl.method = 'POST';
        const formKeys = Object.keys(formObject);
        for (const key of formKeys) {
            const inpEl = document.createElement('input');
            inpEl.name = key;
            inpEl.value = formObject[key];
            inpEl.hidden = true;
            formEl.appendChild(inpEl);
        }
        document.body.appendChild(formEl);
        formEl.submit();
    }

    setShoppingCartV1Visibility(visible: boolean): void {
        const el = document.querySelector(TagName.azaRwsShoppingCartV1) as HTMLElement;
        if (el) {
            this.setElementVisibility(el, visible);
        }
    }

    hasShoppingCartV1MainContainerElement(): boolean {
        const el = this.getDynamicContainerChildElement(this.getAttributeSelector(AttributeName.azaShoppingCart));
        return !!el;
    }

    cleanUpOldShoppingCartElements(): boolean {
        const el = this.getDynamicContainerChildElement(this.getAttributeSelector(AttributeName.azaShoppingCart));
        if (!el) {
            return false;
        }
        el.querySelector('.shopping-cart-title')?.remove();
        el.querySelector(this.getAttributeSelector(AttributeName.azaShoppingCartEmpty))?.remove();
        el.querySelector(this.getAttributeSelector(AttributeName.azaShoppingCartFooter))?.remove();
        return true;
    }

    getPublicCalendarTargetElement(): HTMLElement {
        return this.getDynamicContainerChildElement(this.getAttributeSelector(AttributeName.azaAzavistaCalendarTargetElement));
    }

    setPublicCalendarTargetElementUrl(publicCalendarTargetElement: HTMLElement, url: string): void {
        publicCalendarTargetElement.setAttribute('data-url', url);
    }

    createSpanElement(): HTMLSpanElement {
        return document.createElement('span');
    }

    setActivitiesCartVisibility(visible: boolean): void {
        const el = this.getActivitiesCartElement();
        if (el) {
            this.setElementVisibility(el, visible);
        }
    }

    getActivitiesCartSectionDisplayType(): ActivitiesCartDisplayType {
        const activitiesCartEl = this.getActivitiesCartElement();
        let result: ActivitiesCartDisplayType;
        if (activitiesCartEl) {
            result = activitiesCartEl.parentElement.getAttribute(
                AttributeName.azaActivitiesCartSectionDisplayType
            ) as ActivitiesCartDisplayType;
        }
        return result || ActivitiesCartDisplayType.listView;
    }

    getActivitiesCartShowAddToCalendar(): boolean {
        const activitiesCartEl = this.getActivitiesCartElement();
        let result = false;
        if (activitiesCartEl) {
            result = activitiesCartEl.parentElement.getAttribute(
                AttributeName.azaActivitiesCartSectionShowAddToCalendar
            ) !== null;
        }

        return result;
    }

    getActivitiesCartHideAvailablePlacesLeft(): boolean {
        const activitiesCartEl = this.getActivitiesCartElement();
        let result = false;
        if (activitiesCartEl) {
            result = activitiesCartEl.parentElement.getAttribute(
                AttributeName.azaActivitiesCartSectionHideAvailablePlacesLeft
            ) !== null;
        }

        return result;
    }

    getActivitiesCartElement(): HTMLElement {
        return this.getDynamicContainerChildElement(this.getAttributeSelector(AttributeName.azaActivitiesCart));
    }

    getActivitiesCartElementDependencyId(): string {
        const activitiesCartContainerEl = this.getDynamicContainerChildElement(this.getAttributeSelector(AttributeName.azaActivitiesCartSection));
        if (!activitiesCartContainerEl) {
            return null;
        }
        const attrValue = activitiesCartContainerEl.getAttribute(AttributeName.azaDependency);
        return attrValue?.trim();
    }

    clearActivitiesCartElement(): HTMLElement {
        const activitiesCartEl = this.getActivitiesCartElement();
        if (activitiesCartEl) {
            this.clearElementInnerText(activitiesCartEl);
            // TODO: Temporary solution just to prevent translations to override content we put in
            activitiesCartEl.setAttribute('aza-dont-translate', 'true');
        }
        return activitiesCartEl;
    }

    setActivitiesVisibility(visible: boolean): void {
        const el = this.getActivitiesElement();
        if (el) {
            this.setElementVisibility(el, visible);
        }
    }

    hasActivitiesMainContainerElement(): boolean {
        return !!this.getActivitiesElement();
    }

    getActivitiesElement(): HTMLElement {
        return this.getDynamicContainerChildElement(this.getAttributeSelector(AttributeName.azaActivityListing));
    }

    getMyEventsDynamicContentElement(): HTMLElement {
        return this.getDynamicContainerChildElement(this.getAttributeSelector(AttributeName.azaMyEventsListContentFromDynamicContent));
    }

    clearElementInnerText(el: HTMLElement) {
        if (el) {
            el.innerText = '';
        }
    }

    clearActivitiesElement(): HTMLElement {
        const activitiesEl = this.getActivitiesElement();
        this.clearElementInnerText(activitiesEl);
        return activitiesEl;
    }

    clearMyEventsDynamicContainerElement(): { contentElement: HTMLElement, sectionElement: HTMLElement } {
        const myEventsEl = this.getMyEventsDynamicContentElement();
        this.clearElementInnerText(myEventsEl);
        return { contentElement: myEventsEl, sectionElement: myEventsEl?.parentElement };
    }

    getShoppingCartProductsElement(): HTMLElement {
        return this.getDynamicContainerChildElement(this.getAttributeSelector(AttributeName.azaShoppingCartProducts));
    }

    clearShoppingCartV1ProductsElement(): void {
        const shoppingCartProductsEl = this.getShoppingCartProductsElement();
        this.clearElementInnerText(shoppingCartProductsEl);
    }

    clearShoppingCartV2ContainerElement(): void {
        const shoppingCartV2ContainerEl = this.getShoppingCartV2ContainerElement();
        this.clearElementInnerText(shoppingCartV2ContainerEl);
    }

    setShoppingCartV2Visibility(visible: boolean): void {
        const el = document.querySelector(TagName.azaRwsShoppingCartV2) as HTMLElement;
        if (el) {
            this.setElementVisibility(el, visible);
        }
    }

    hasShoppingCartV2MainContainerElement(): boolean {
        const el = this.getDynamicContainerChildElement(this.getAttributeSelector(AttributeName.azaShoppingCartV2));
        return !!el;
    }

    shouldShowShoppingCartV2DiscountInput(): boolean {
        const el = this.getShoppingCartV2SectionElement();
        if (!el) {
            return false;
        }
        return el.hasAttribute(AttributeName.azaShoppingCartV2SectionDisplayDiscountField);
    }

    moveShoppingCartV2ComponentElementToDynamicShoppingCartElementPlaceholder(): void {
        const shoppingCartV2ComponentEl = document.querySelector(TagName.azaRwsShoppingCartV2);
        const shoppingCartV2ContainerEl = this.getShoppingCartV2ContainerElement();
        if (shoppingCartV2ContainerEl && shoppingCartV2ComponentEl) {
            shoppingCartV2ContainerEl.appendChild(shoppingCartV2ComponentEl);
        }
    }

    restoreShoppingCarV2tComponentParent(): void {
        if (!this.dynamicEl) {
            return;
        }
        const shoppingCartV2ComponentEl = this.getDynamicContainerChildElement(TagName.azaRwsShoppingCartV2);
        if (shoppingCartV2ComponentEl) {
            const shoppingCartV2ComponentContainerEl = document.getElementById(ElementId.azaRwsShoppingCartV2ComponentContainer);
            if (shoppingCartV2ComponentContainerEl) {
                shoppingCartV2ComponentContainerEl.appendChild(shoppingCartV2ComponentEl);
            }
        }
    }

    getShoppingCartV2ContainerElement(): HTMLElement {
        return this.getDynamicContainerChildElement(this.getAttributeSelector(AttributeName.azaShoppingCartV2));
    }

    getShoppingCartV2SectionElement(): HTMLElement {
        return this.getDynamicContainerChildElement(this.getAttributeSelector(AttributeName.azaShoppingCartV2Section));
    }

    moveShoppingCartV1ComponentElementToDynamicShoppingCartElementPlaceholder(): void {
        const shoppingCartComponentEl = document.querySelector(TagName.azaRwsShoppingCartV1);
        const shoppingCartProductsEl = this.getShoppingCartProductsElement();
        if (shoppingCartProductsEl && shoppingCartComponentEl) {
            shoppingCartProductsEl.appendChild(shoppingCartComponentEl);
        }
    }

    restoreShoppingCartComponentParent(): void {
        if (!this.dynamicEl) {
            return;
        }
        const shoppingCartComponentEl = this.getDynamicContainerChildElement(TagName.azaRwsShoppingCartV1);
        if (shoppingCartComponentEl) {
            const shoppingCartComponentContainerEl = document.getElementById(ElementId.azaRwsShoppingCartV1ComponentContainer);
            if (shoppingCartComponentContainerEl) {
                shoppingCartComponentContainerEl.appendChild(shoppingCartComponentEl);
            }
        }
    }

    setMultiRegistrationVisibility(visible: boolean): void {
        const el = document.querySelector(TagName.azaRwsMultiRegistration) as HTMLElement;
        if (el) {
            this.setElementVisibility(el, visible);
        }
    }

    moveMultiRegistrationComponentElementToDynamicElementPlaceholder(): void {
        const componentEl = document.querySelector(`.aza-rws-multi-registration-component-container ${TagName.azaRwsMultiRegistration}`);
        const containerEl = this.getSectionMultiRegistration();
        if (containerEl && componentEl) {
            const sectionEl = containerEl.querySelector('.aza-section-content');
            if (sectionEl) {
                sectionEl.innerHTML = '';
                sectionEl.appendChild(componentEl);
            }
        }
    }

    getSectionMultiRegistration() {
        return this.dynamicEl.querySelector('[class*=section-multi-registration]');
    }

    restoreMultiRegistrationComponentParent(): void {
        if (!this.dynamicEl) {
            return;
        }
        const componentEl = this.getDynamicContainerChildElement(TagName.azaRwsMultiRegistration);
        if (componentEl) {
            const containerEl = document.getElementById(ElementId.azaRwsMultiRegistrationComponentContainer);
            if (containerEl) {
                containerEl.appendChild(componentEl);
            }
        }
    }

    moveActivitiesCartComponentElementToDynamicActivitiesCartElementPlaceholder(): boolean {
        const activitiesCartComponentEl = document.querySelector(TagName.azaRwsActivitiesCart);
        const activitiesCartEl = this.getActivitiesCartElement();
        if (activitiesCartEl && activitiesCartComponentEl) {
            activitiesCartEl.appendChild(activitiesCartComponentEl);
            return true;
        }
        return false;
    }

    restoreActivitiesCartComponentParent(): void {
        if (!this.dynamicEl) {
            return;
        }
        const acivitiesCartComponentEl = this.getDynamicContainerChildElement(TagName.azaRwsActivitiesCart);
        if (acivitiesCartComponentEl) {
            const activitiesCartComponentContainerEl = document.getElementById(ElementId.azaRwsActivitiesCartComponentContainer);
            if (activitiesCartComponentContainerEl) {
                activitiesCartComponentContainerEl.appendChild(acivitiesCartComponentEl);
            }
        }
    }

    restoreMyEventsContainerParent(): void {
        if (!this.dynamicEl) {
            return;
        }
        const myEventsContainerEl = this.getDynamicContainerChildElement(this.getAttributeSelector(AttributeName.azaRwsMyEventsContainer));
        if (myEventsContainerEl) {
            const myEventsComponentContainerEl = document.getElementById(ElementId.azaRwsMyEventsComponentContainer);
            if (myEventsComponentContainerEl) {
                myEventsComponentContainerEl.appendChild(myEventsContainerEl);
            }
        }
    }

    moveActivityListComponentElementToDynamicActivityListElementPlaceholder(): boolean {
        const activityListComponentEl = document.querySelector(TagName.azaRwsActivities);
        const activityListEl = this.getActivitiesElement();
        if (activityListEl && activityListComponentEl) {
            activityListEl.appendChild(activityListComponentEl);
            return true;
        }
        return false;
    }

    moveMyEventsContainerElementToDynamicMyEventsElementPlaceholder(): boolean {
        const myEventsLocalContainerEl = document.querySelector(this.getAttributeSelector(AttributeName.azaRwsMyEventsContainer));
        const myEventsDynamicContentEl = this.getMyEventsDynamicContentElement();
        if (myEventsDynamicContentEl && myEventsLocalContainerEl) {
            myEventsDynamicContentEl.appendChild(myEventsLocalContainerEl);
            return true;
        }
        return false;
    }

    restoreActivityListComponentParent(): void {
        if (!this.dynamicEl) {
            return;
        }
        const acivityListComponentEl = this.getDynamicContainerChildElement(TagName.azaRwsActivities);
        if (acivityListComponentEl) {
            const activityListComponentContainerEl = document.getElementById(ElementId.azaRwsActivitiesComponentContainer);
            if (activityListComponentContainerEl) {
                activityListComponentContainerEl.appendChild(acivityListComponentEl);
            }
        }
    }

    setGoToActiveFormButtonsDisabledClass(language: string): void {
        const internalButtons = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaGoToPage));
        internalButtons.forEach(el => {
            const internalButtonAttrValue = el.getAttribute(AttributeName.azaGoToPage);

            if (internalButtonAttrValue === SpecialPageId.azaForm) {
                this.addClass(el, ClassName.azaDisabledButton);
                this.addAttribute(el, AttributeName.disabled, '');
                // this is a hack for HM
                if (el.innerHTML === 'Registration' && language === 'en-US') {
                    el.removeAttribute('data-azavista-translatable');
                    el.innerHTML = 'Registration Closed';
                }
            }
        });
    }

    attachScriptToGoToPageButtons(callback: (pageId: string, additionalQueryParams: QueryParamsInput | null) => void): void {
        const internalButtons = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaGoToPage));
        internalButtons.forEach(el => {
            const internalButtonAttrValue = el.getAttribute(AttributeName.azaGoToPage);
            const additionalQueryParamsString = el.getAttribute(AttributeName.dataAdditionalQueryParams);
            if (internalButtonAttrValue) {
                // Do not attach event if the button is disabled
                const isDisabled = el.classList.contains(ClassName.azaDisabledButton) || el.getAttribute(AttributeName.disabled) !== null;
                if (!isDisabled) {
                    el.addEventListener('click', ev => {
                        ev.preventDefault();
                        ev.stopPropagation();
                        callback(internalButtonAttrValue, additionalQueryParamsString);
                    });
                }
            }
        });
    }

    getValueTranslation(value: string, language: string, attributeTranslations: IAttributeTranslation[]): string {
        if (!attributeTranslations || !Array.isArray(attributeTranslations)) {
            return value;
        }
        const attrTranslation = attributeTranslations.find(x => x.value === value);
        if (!attrTranslation || !attrTranslation.trans) {
            return value;
        }
        return attrTranslation.trans[language] || value;
    }

    setSelectElementsOptions(fields: IField[], language: string, customParentElement?: HTMLElement): void {
        const elementSelector = 'select';
        const allSelectEls = customParentElement ? customParentElement.querySelectorAll(elementSelector) : this.getDynamicContainerChildrenElements(elementSelector);
        allSelectEls.forEach(el => {
            const fieldIdFromElementAtribute = el.getAttribute(AttributeName.fieldId);
            if (fieldIdFromElementAtribute) {
                const field = fields.find(x => x.id === fieldIdFromElementAtribute);
                if (field) {
                    const items = (field.schema.enum || field.schema.items?.enum) || [];
                    const size = items.length <= 5 ? 5 : items.length;
                    if (el.hasAttribute('multiple')) {
                        el.setAttribute('size', size.toString());
                    }
                    // single-select radioButtons will be displayed as radioButtons by using CSS
                    // that is controlled via `.aza-input-type-select-radiobuttons` selector
                    const isSingleSelectRadioButtons = field.type === 'select' && field.component === 'radio_buttons' as any;
                    el.classList.toggle(ClassName.azaInputTypeSelectRadioButtons, isSingleSelectRadioButtons);
                    // multi-select checkbox will be displayed as checkbox by using CSS
                    // that is controlled via `.aza-input-type-multi-select-checkboxes` selector
                    const isCheckBoxes = field.component === 'checkboxes' as any;
                    el.classList.toggle(ClassName.azaInputTypeMultiSelectCheckboxes, isCheckBoxes);
                    const selectEl = el as HTMLSelectElement;
                    this.clearSelectElementOptions(selectEl);
                    for (const item of items) {
                        const optionEl = document.createElement('option');
                        optionEl.value = item;
                        if (this.isSelectFieldValueDisabled(field, item)) {
                            optionEl.disabled = true;
                        }
                        optionEl.text = this.getValueTranslation(optionEl.value, language, field.attributeTranslations);
                        selectEl.options.add(optionEl);
                        if (isCheckBoxes || isSingleSelectRadioButtons) {
                            const inputElement = document.createElement('input');
                            inputElement.type = isCheckBoxes ? 'checkbox' : 'radio';
                            inputElement.name = `${inputElement.type}_${field.name}${isCheckBoxes ? '[]' : ''}`;
                            inputElement.id = isCheckBoxes ? getCheckboxId(field, item) : getRadioId(field, item);
                            inputElement.className = 'auto-width';
                            const inputLabel = document.createElement('label');
                            inputLabel.htmlFor = inputElement.id;
                            inputLabel.innerHTML = optionEl.text;
                            inputLabel.onclick = (ev) => {
                                // intercept the click event
                                // because in the multi-registration, there are multiple input-field-element with same `id` attribute
                                // that caused the `label.htmFor` to always target the first occurrence
                                ev.stopImmediatePropagation();
                                ev.stopPropagation();
                                ev.preventDefault();
                                simulateClickEvent(inputElement);
                            };
                            inputElement.onclick = () => {
                                const latestOptionValue = optionEl.selected;
                                simulateClickEvent(optionEl);
                                if (isSingleSelectRadioButtons) {
                                    selectEl.value = item;
                                    optionEl.selected = true;
                                    selectEl.dispatchEvent(new Event('input', { bubbles: true }));
                                }
                                // double check whether the latest option change is triggered
                                delay(0).then(() => {
                                    if (latestOptionValue === optionEl.selected) {
                                        optionEl.selected = !latestOptionValue;
                                        selectEl.dispatchEvent(new Event('input', { bubbles: true }));
                                    }
                                });
                            };
                            const div = document.createElement('div');
                            div.append(inputElement, inputLabel);
                            selectEl.parentElement.append(div);
                        }
                    }
                }
            }
        });
    }

    setSelectElementValueOptionsTranslations(fields: IField[], language: string, customParentElement: HTMLElement = this.getDynamicContentContainerElement()): void {
        fields?.forEach(field => {
            const type = fieldType(field);
            if (type === 'select' || (type === 'multi-select')) {
                const selectELs = customParentElement.querySelectorAll(`select[${AttributeName.fieldId}='${field.id}']`);
                selectELs.forEach((selectEl: HTMLSelectElement) => {
                    const options = selectEl.options;
                    for (let i = 0; i < options.length; i++) {
                        const optionEl = options.item(i);
                        const translatedText = this.getValueTranslation(optionEl.value, language, field.attributeTranslations);
                        optionEl.text = translatedText;
                    }
                });
                const enumItems = (type === 'select' ? field.schema.enum : field.schema.items?.enum) || [];
                enumItems.forEach(item => {
                    const translatedText = this.getValueTranslation(item, language, field.attributeTranslations);
                    const radioForValue = getRadioId(field, item, true);
                    const radios = customParentElement.querySelectorAll(`label[for="${radioForValue}"]`);
                    radios.forEach((radio: HTMLLabelElement) => {
                        radio.textContent = translatedText;
                    });
                    const checkboxForValue = getCheckboxId(field, item, true);
                    const checkboxes = customParentElement.querySelectorAll(`label[for="${checkboxForValue}"]`);
                    checkboxes.forEach((checkbox: HTMLLabelElement) => {
                        checkbox.textContent = translatedText;
                    });
                });

            }
        });
    }

    isSelectFieldValueDisabled(field: IField, value: string): boolean {
        return !!field.options?.selectItems?.valueStates?.[value]?.disabled;
    }

    setSelectFieldsTranslations(fields: IField[], language: string): void {
        const multiSelectEls = this.getDynamicContainerChildrenElements('select');
        multiSelectEls.forEach((el: HTMLSelectElement) => {
            const fieldIdFromElementAtribute = el.getAttribute(AttributeName.fieldId);
            if (fieldIdFromElementAtribute) {
                const field = fields.find(x => x.id === fieldIdFromElementAtribute);
                if (field) {
                    for (let i = 0; i < el.options.length; i++) {
                        const optionEl = el.options.item(i);
                        optionEl.text = this.getValueTranslation(optionEl.value, language, field.attributeTranslations);
                    }
                }
            }
        });
    }

    clearSelectElementOptions(el: HTMLSelectElement): void {
        for (let i = el.options.length - 1; i >= 0; i--) {
            el.options.remove(i);
        }
    }

    attachScriptToMultiSelectOptions(): void {
        const multiSelectEls = this.getDynamicContainerChildrenElements('select[multiple]');
        multiSelectEls.forEach((selectEl: HTMLSelectElement) => {
            selectEl.addEventListener('mousedown', async ev => {
                if (ev.target instanceof HTMLOptionElement) {
                    const scrollTopToRestore = selectEl.scrollTop;
                    const targetEl = ev.target;
                    targetEl.selected = !targetEl.selected;
                    ev.preventDefault();
                    // We must validate the multi-select field because preventDefault() will prevent calling oninput
                    // which causes the valaidation to trigger
                    this.triggerInputEvent(targetEl.parentElement);
                    await delay(0);
                    selectEl.scrollTop = scrollTopToRestore;
                }
            });
        });
    }

    replaceTranslationsMergetags(
        translations: any,
        participantObj: IObjectWithId, participantFields: IField[], eventObj: IObjectWithId, eventFields: IField[],
        eventSettings: IEventSettings,
    ): void {
        const languages = Object.keys(translations);
        for (const lang of languages) {
            const translationKeys = Object.keys(translations[lang]);
            for (const key of translationKeys) {
                translations[lang][key] = this.replaceMergetags(
                    translations[lang][key], participantObj, participantFields, eventObj, eventFields, eventSettings, lang
                );
            }
        }
    }

    replaceMergetags(
        html: string,
        participantObj: IObjectWithId, participantFields: IField[], eventObj: IObjectWithId, eventFields: IField[],
        eventSettings: IEventSettings, language: string
    ): string {
        let replacedHtml = html;

        // Replace participant fields mergetags
        replacedHtml = this.replaceMergetagsWithObjectValues(
            replacedHtml, MergetagPrefix.participant, MergetagSuffix.common, participantObj, participantFields, eventSettings, language
        );

        // TODO: If replacement of participant fields mergetags results in event mergetag (like participant field value is [[event.name]])
        //       it will be replaced. To avoid this, we must find all mergetags positions and lengths and then construct
        //       string with the content "around" mergetags and replacements of the mergtags in their place
        // Replace event fields mergetags
        replacedHtml = this.replaceMergetagsWithObjectValues(
            replacedHtml, MergetagPrefix.event, MergetagSuffix.common, eventObj, eventFields, eventSettings, language
        );
        return replacedHtml;
    }

    replaceMergetagsWithObjectValues(
        html: string, mergetagPrefix: string, mergetagSuffix: string, obj: IObjectWithId, objFields: IField[],
        eventSettings: IEventSettings, language: string
    ): string {
        let replacedHtml = html;
        const allMergetags = this.getAllMergetags(html, mergetagPrefix, mergetagSuffix);
        for (const item of allMergetags) {
            const fieldName = this.getFieldNameFromMergetag(item.mergetag, mergetagPrefix, mergetagSuffix);
            const field = objFields.find(x => x.name === fieldName);
            if (field) {
                const formattedValue = this.getFormattedFieldValueString(
                    obj[field.id], field, language, eventSettings
                );
                const escapedFieldValue = this.utilsSvc.escapeHtml(formattedValue);
                // TODO: We can use String.prototype.replaceAll in the future for performance optimization
                replacedHtml = replacedHtml.split(item.mergetag).join(escapedFieldValue);
            }
        }
        return replacedHtml;
    }

    getFieldNameFromMergetag(mergetag: string, prefix: string, suffix: string): string {
        const fieldNameStart = prefix.length;
        const fieldNameLength = mergetag.length - prefix.length - suffix.length;
        const fieldName = mergetag.substr(fieldNameStart, fieldNameLength);
        return fieldName;
    }

    getAllMergetags(html: string, prefix: string, suffix: string): IMergetagPosition[] {
        const allMergetags: IMergetagPosition[] = [];
        let mergetagPos: IMergetagPosition = { start: 0, length: 0, mergetag: null };
        while (true) {
            mergetagPos = this.findMergetag(html, prefix, suffix, mergetagPos.start + mergetagPos.length);
            if (mergetagPos.length > 0) {
                allMergetags.push(mergetagPos);
            } else {
                break;
            }
        }
        return allMergetags;
    }

    findMergetag(html: string, prefix: string, suffix: string, start: number): IMergetagPosition {
        const result: IMergetagPosition = { start: -1, length: 0, mergetag: null };
        const prefixIndex = html.indexOf(prefix, start);
        if (prefixIndex === -1) {
            return result;
        }
        const suffixIndex = html.indexOf(suffix, prefixIndex + 1);
        if (start >= 0 && suffixIndex >= 0) {
            result.start = prefixIndex;
            const end = suffixIndex + suffix.length;
            result.length = end - prefixIndex;
            result.mergetag = html.substr(result.start, result.length);
        }
        return result;
    }

    // replaceMergetags(
    //     participantObj: IObjectWithId, participantFields: IField[], eventObj: IObjectWithId, eventFields: IField[],
    //     eventSettings: IEventSettings, language: string
    // ): void {
    //     if (!participantObj) {
    //         return;
    //     }
    //     const mergetagsEls = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.mergetag));
    //     mergetagsEls.forEach(el => {
    //         if (this.isContentMergetagLike(el)) {
    //             this.replaceMergetagElementContent(
    //               el, participantObj, participantFields, eventObj, eventFields, eventSettings, language
    //             );
    //         }
    //     });
    // }

    // replaceMergetagElementContent(
    //     el: HTMLElement, participantObj: IObjectWithId, participantFields: IField[], eventObj: IObjectWithId, eventFields: IField[],
    //     eventSettings: IEventSettings, language: string
    // ): void {
    //     const fieldId = el.getAttribute(AttributeName.fieldId);
    //     let obj: IObjectWithId;
    //     let fields: IField[];
    //     let convertTimeZone: boolean;
    //     const fieldType = el.getAttribute(AttributeName.fieldType);
    //     if (fieldType === MergetagFieldType.participant) {
    //         obj = participantObj;
    //         fields = participantFields;
    //         convertTimeZone = false;
    //     } else {
    //         obj = eventObj;
    //         fields = eventFields;
    //         convertTimeZone = true;
    //     }
    //     const field = fields.find(x => x.id === fieldId);
    //     const formattedValue = this.getFormattedFieldValueString(obj[fieldId], field, language, eventSettings, convertTimeZone);
    //     el.innerText = formattedValue;
    // }

    getFormattedFieldValueString(
        value: any, field: IField, language: string, eventSettings: IEventSettings
    ): string {
        if (!value) {
            return '';
        }
        if (!field) {
            return value || '';
        }
        const fieldType = this.utilsSvc.getFieldTypeFromField(field);
        // Date, time and date-time values must be formatted according to event display formats
        if (fieldType === 'date') {
            return this.momentSvc.formatDate(value, language, eventSettings.date_format);
        } else if (fieldType === 'datetime') {
            return this.momentSvc.formatDateTime(value, eventSettings.timezone, language, eventSettings.datetime_format);
        } else if (fieldType === 'travel-date') {
            // return this.momentSvc.formatDateTimeNoTimezone(value, language, eventSettings.datetime_format);
            return this.momentSvc.formatDateTime(value, 'UTC', language, eventSettings.datetime_format);
        } else if (fieldType === 'time') {
            return this.momentSvc.formatTime(value, language, eventSettings.time_format);
        }
        if (value) { // } && fieldType !== 'string') {
            return value.toString();
        }
        return value || '';
    }

    isContentMergetagLike(el: HTMLElement): boolean {
        if (el.childNodes.length === 1 && el.childNodes[0].nodeType === document.TEXT_NODE) {
            // This is a valid mergetag element - it has only one child of type text node
            return true;
        }
        return false;
    }

    setErrorHeaderContent(html: string): void {
        this.setDynamicContent(`<h1 class="reg-app-error-content">${html}</h1>`);
    }

    addAttribute(element: Element, attributeName: string, attributeValue: string): void {
        element.setAttribute(attributeName, attributeValue);
    }

    removeAttribute(element: Element, attributeName: string): void {
        element.removeAttribute(attributeName);
    }

    addClass(element: Element, className: string): void {
        element.classList.add(className);
    }

    removeClass(element: Element, className: string): void {
        element.classList.remove(className);
    }

    containsClass(element: Element, className: string): boolean {
        return element.classList.contains(className);
    }

    getBodyElement(): HTMLElement {
        return document.querySelector('body');
    }

    hideBodyScrolls(): void {
        this.addClass(this.getBodyElement(), ClassName.noScroll);
    }

    restoreBodyScrolls(): void {
        this.removeClass(this.getBodyElement(), ClassName.noScroll);
    }

    getLanguageSelectElements(): NodeListOf<HTMLSelectElement> {
        const langContainerEl = this.getDynamicContainerChildrenElements(
            'select' + this.getAttributeSelector(AttributeName.azaLanguageSelector)
        ) as NodeListOf<HTMLSelectElement>;
        return langContainerEl;
    }

    setSelectedLanguageItem(languageId: string): void {
        const selectEls = this.getLanguageSelectElements();
        if (selectEls.length === 0) {
            return;
        }
        selectEls.forEach(selectEl => {
            for (let i = 0; i < selectEl.options.length; i++) {
                const optionItemEl = selectEl.options.item(i);
                optionItemEl.selected = optionItemEl.value === languageId;
            }
        });
    }

    setPreferredLanguageSelectOptions(preferredLanguageField: IField, eventLanguages: ILanguageItem[]): void {
        const allPreferredLanguageInputFields = this.getAllInputElementsByFieldId(preferredLanguageField.id);
        for (const el of allPreferredLanguageInputFields) {
            if (el instanceof HTMLSelectElement) {
                const isSelect = el.type === 'select-one';
                if (isSelect) {
                    this.setSelectElementLanguages(el, eventLanguages);
                }
            }
        }
    }

    setDocumentLanguageAndDirectionAttributes(language: string): void {
        const htmlEl = document.querySelector('html');
        if (htmlEl.lang !== language) {
            htmlEl.lang = language;
        }
        // Change the editor HTML dir attribute according to the language
        const languageDirection = this.sharedSvc.getLanguageDirection(language);
        const currentDirection = htmlEl.dir;
        if (currentDirection !== languageDirection) {
            htmlEl.dir = languageDirection;
        }
    }

    setLanguageItemsToLanguageDropDowns(languages: ILanguageItem[], langChangedCallback: (lang: string) => void): void {
        /*
        Sample:
        <select aza-language-container>
        </select>
        */
        const langContainerEls = this.getLanguageSelectElements();
        if (langContainerEls.length === 0) {
            return;
        }

        if (!languages) {
            return;
        }

        langContainerEls.forEach(langContainerEl => {
            this.setSelectElementLanguages(langContainerEl, languages);
            langContainerEl.onchange = e => {
                if (langChangedCallback) {
                    langChangedCallback((e.target as HTMLSelectElement).value);
                }
            };
        });
    }

    setSelectElementLanguages(el: HTMLSelectElement, languages: ILanguageItem[]): void {
        for (let i = el.options.length - 1; i >= 0; i--) {
            el.options.remove(i);
        }
        for (const language of languages) {
            const optionEl = document.createElement('option');
            optionEl.value = language.id;
            optionEl.text = language.label;
            el.options.add(optionEl);
        }
    }

    showTextInMessageElements(text: string): void {
        const msgEls = this.getMessageElements();
        if (msgEls.length === 0) {
            return;
        }
        msgEls.forEach(el => {
            el.innerText = text;
            this.showElement(el);
        });
    }

    hideMessageElements(): void {
        const els = this.getMessageElements();
        els.forEach(el => this.hideElement(el));
    }

    getMessageElements(): NodeListOf<HTMLElement> {
        const msgEls = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaMessage));
        return msgEls;
    }

    disableAllEmailInputElements(fields: IField[]): void {
        const emailField = this.appSvc.getEmailField(fields);
        if (emailField) {
            const inputEls = this.getAllInputElementsByFieldId(emailField.id);
            inputEls.forEach(el => {
                try {
                    (el as HTMLInputElement).disabled = true;
                } catch (err) {
                    // TODO: Can't disable field
                    this.appLoggerSvc.log(LogLevel.error, err);
                }
            });
        }
    }

    setAllInputElementsValue(field: IField, value: any, parentEl?: HTMLElement): void {
        parentEl ||= this.getDynamicContentContainerElement();
        const els = this.getAllInputElementsByFieldId(field.id, parentEl);
        els.forEach(el => {
            try {
                this.setFormInputElementValue(field, el, value);
            } catch (err) {
                // TODO: Can't set the value
                this.appLoggerSvc.log(LogLevel.error, err);
            }
        });
    }

    getAllInputElementsByFieldId(fieldId: string, parentEl?: HTMLElement): HTMLElement[] {
        const result: HTMLElement[] = [];
        parentEl ||= this.getDynamicContentContainerElement();
        const inpEls = parentEl.querySelectorAll(
            this.getAttributeWithValueSelector(AttributeName.fieldId, fieldId)
        ) as NodeListOf<HTMLElement>;
        if (inpEls.length === 0) {
            return [];
        }
        inpEls.forEach(el => {
            const isCandidate = this.isInputElementSettingValueCandidate(el);
            if (isCandidate) {
                result.push(el);
            }
        });
        return result;
    }

    getAllDocumentElementsByFieldId(fieldId: string, parentEl?: HTMLElement): HTMLElement[] {
        const result: HTMLElement[] = [];
        parentEl ||= this.getDynamicContentContainerElement();
        const fieldIdAttributeSelector = this.getAttributeWithValueSelector(AttributeName.fieldId, fieldId);
        const documentFieldSelector = `[${AttributeName.azaDocumentField}]${fieldIdAttributeSelector}`;
        const elements = parentEl.querySelectorAll(documentFieldSelector) as NodeListOf<HTMLElement>;
        if (elements.length === 0) {
            return [];
        }
        elements.forEach(x => result.push(x));
        return result;
    }

    getAllImageElementsByFieldId(fieldId: string, parentEl?: HTMLElement): HTMLElement[] {
        const result: HTMLElement[] = [];
        parentEl ||= this.getDynamicContentContainerElement();
        const fieldIdAttributeSelector = this.getAttributeWithValueSelector(AttributeName.fieldId, fieldId);
        const imageFieldSelector = `[${AttributeName.azaImageField}]${fieldIdAttributeSelector}`;
        const elements = parentEl.querySelectorAll(imageFieldSelector) as NodeListOf<HTMLElement>;
        if (elements.length === 0) {
            return [];
        }
        elements.forEach(x => result.push(x));
        return result;
    }

    getImageFieldTypeParent(childEl: HTMLElement): HTMLElement | null {
        return childEl.closest(this.getAttributeSelector(AttributeName.azaImageField));
    }

    getAllAnchorElementsByFieldId(fieldId: string, parentEl?: HTMLElement): HTMLElement[] {
        const result: HTMLElement[] = [];
        parentEl ||= this.getDynamicContentContainerElement();
        const els = parentEl.querySelectorAll(
            this.getAttributeWithValueSelector(AttributeName.id, `${fieldId}-url-link`)
        ) as NodeListOf<HTMLElement>;
        if (els.length === 0) {
            return [];
        }
        els.forEach(el => result.push(el));
        return result;
    }

    getInputElementByFieldId(fieldId: string, parentEl?: HTMLElement): HTMLElement {
        parentEl ||= this.getDynamicContentContainerElement();
        const nonMultiRegFormSelector = parentEl === this.getDynamicContentContainerElement() ? ':not(.section-multi-registration-form *) ' : '';
        const inputElementSelector = `${this.getAttributeWithValueSelector(AttributeName.fieldId, fieldId)}${nonMultiRegFormSelector}`;
        const inpEl: HTMLElement = parentEl.querySelector(inputElementSelector);
        const isCandidate = this.isInputElementSettingValueCandidate(inpEl);
        if (!isCandidate) {
            return null;
        }
        return inpEl;
    }

    isInputElementSettingValueCandidate(el: HTMLElement): boolean {
        if (!el) {
            return false;
        }
        if (el.nodeName.toLowerCase() === 'label') {
            return false;
        }
        if (el.getAttribute(AttributeName.azaMergetag) !== null) {
            // Mergetag element also has field-id attribute but should not be treated as input element
            return false;
        }
        if (el.getAttribute(AttributeName.azaDocumentField) !== null) {
            // Document field also has field-id attribute but should not be treated as input element
            return false;
        }

        if (el.getAttribute(AttributeName.azaImageField) !== null) {
            // Image field also has field-id attribute but should not be treated as input element
            return false;
        }
        return true;
    }

    executeFnOnInputElements(
        entity: any,
        fields: IField[],
        callbackFn: (param: { inputEls: HTMLElement[], anchorEls: HTMLElement[], field: IField }) => void,
        parentEl?: HTMLElement
    ): void {
        if (!entity) {
            return;
        }
        parentEl ||= this.getDynamicContentContainerElement();
        for (const field of fields) {
            const inputEls = this.getAllInputElementsByFieldId(field.id, parentEl);
            const anchorEls = this.getAllAnchorElementsByFieldId(field.id, parentEl);
            callbackFn({
                inputEls,
                anchorEls,
                field,
            });
        }
    }

    fillInputElements(entity: any, fields: IField[], parentEl?: HTMLElement): void {
        if (parentEl) {
            const {width, height} = parentEl.getBoundingClientRect();
            if (!width && !height) {
                // If parent element is not visible, do not attempt to fill input elements
                // this is to fix case on multiregistration form field of a radio or checkbox
                return;
            }
        }
        this.executeFnOnInputElements(
                entity,
                fields,
                ({ inputEls, anchorEls, field }) => {
                    let value = entity[field.id];
                    inputEls.forEach(el => {
                        try {
                            if (!value && field.schema && field.schema.default) {
                                value = field.schema.default;
                            }
                            this.setFormInputElementValue(field, el, value, parentEl);
                            this.setUpdateUrlLinkCallbackForInputElement(el, anchorEls);
                        } catch (err) {
                            this.appLoggerSvc.log(LogLevel.error, err);
                        }
                    });
                    anchorEls.forEach(el => {
                        try {
                            this.setElementHref(el, value as string);
                        } catch (err) {
                            this.appLoggerSvc.log(LogLevel.error, err);
                        }
                    });
                },
                parentEl,
        );
    }

    getSubmitButtons(): HTMLButtonElement[] | null {
        const formEl = this.getDynamicContainerChildrenElements('.section-form-button') as NodeListOf<HTMLElement>;
        const buttonElements: HTMLButtonElement[] = [];
        formEl.forEach(el => {
            const attrWithValueSelector = this.getAttributeWithValueSelector(AttributeName.type, 'submit');
            const submitBtn = el.querySelector('button' + attrWithValueSelector) as HTMLButtonElement;
            buttonElements.push(submitBtn);
        });
        return buttonElements;
    }

    getCancellationButton(): HTMLButtonElement | null {
        const btnEl = this.getDynamicContainerChildElement(
            this.getAttributeSelector(AttributeName.azaCancellationButton)
        ) as HTMLButtonElement;
        return btnEl;
    }

    getDependencyElements(): Record<string, HTMLElement[]> {
        const depElements = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.azaDependency));
        const depElementsMap: Record<string, HTMLElement[]> = {};
        depElements.forEach(x => {
            const dependencyId = x.getAttribute(AttributeName.azaDependency).trim();
            if (dependencyId) {
                if (depElementsMap[dependencyId]) {
                    depElementsMap[dependencyId].push(x);
                } else {
                    depElementsMap[dependencyId] = [x];
                }
            }
        });
        return depElementsMap;
    }

    getReferencedButMissingDependencyIds(dependencies: IDependency[], activities: IActivity[]): string[] {
        const activitiesWithDependencyId: IActivityWithDependencyId[] = activities;
        const dependencyHTMLElements = this.getDependencyElements();
        const htmlElementDependencyIds = Object.keys(dependencyHTMLElements);
        const activityDependencyIds = activitiesWithDependencyId.filter(x => !!x.dependency_id).map(x => x.dependency_id);
        const allReferencedDependencyIds = [...htmlElementDependencyIds, ...activityDependencyIds];

        const missingDependencyIds: string[] = [];
        for (const id of allReferencedDependencyIds) {
            if (!dependencies.find(x => x.id === id) && !missingDependencyIds.includes(id)) {
                missingDependencyIds.push(id);
            }
        }
        return missingDependencyIds;
    }

    /**
     * Applies dependencies and returns field ids which were hidden
     */
    applyDependencies(
        fields: IField[],
        dependencies: IDependency[],
        participant: IObjectWithId,
        userProvidedImageFieldIds: string[],
        userDeletedImageFieldIds: string[],
        userProvidedDocumentFieldIds: string[],
        userDeletedDocumentFieldIds: string[],
        event: IObjectWithId,
        activities: IActivityList[],
        shoppingCartResult: ShoppingCartResultUI,
        workflow: Workflow,
        eventSettings: EventSettings,
    ): IApplyDependenciesResult {
        const result: IApplyDependenciesResult = {
            dependencyResults: [], hiddenFieldIds: [], dependencies: dependencies, activities: activities
        };
        // First find dependencies which are used in the form and activities but not provided by the server
        // Such dependencies are deleted but still referenced - we will treat such dependencies as fulfilled
        const referencedButMissingDependencyIds = this.getReferencedButMissingDependencyIds(dependencies, activities);
        for (const referencedButMissingDependencyId of referencedButMissingDependencyIds) {
            const missingDependency = { id: referencedButMissingDependencyId } as IDependency;
            result.dependencyResults.push({ dependency: missingDependency, result: true, isDependencyReferencedButMissing: true });
        }

        if (!fields || fields.length === 0) {
            return result;
        }
        const activitiesWithDependencyId: IActivityWithDependencyId[] = activities;
        const activityDependencyIds = activitiesWithDependencyId.filter(x => x.dependency_id).map(x => x.dependency_id);
        const dependenciesFound = dependencies.length > 0 || activityDependencyIds.length > 0;
        if (!dependenciesFound) {
            return result;
        }
        // Use only dependencies that are found in current HTML
        const dependencyHTMLElements = this.getDependencyElements();

        // TODO: We can have dependencies which are not used in HTML elements - like product dependencies
        //       These are dependencies attached to product groups and they should show/hide items in the shopping cart,
        //       not HTML elements in the web page
        //const usedDependencyIds = Object.keys(dependencyHTMLElements);
        const usedDependencyIds = dependencies.map(x => x.id);

        // TODO: The @azavista/activity-lib does not define dependency_id in the IActivity
        //       Add it here but remove it when the library is updated to contain the dependency_id attribute
        for (const activityDependencyId of activityDependencyIds) {
            if (!usedDependencyIds.includes(activityDependencyId)) {
                usedDependencyIds.push(activityDependencyId);
            }
        }

        const usedDependencies = this.utilsSvc.clone(dependencies.filter(x => usedDependencyIds.indexOf(x.id) >= 0));
        // We need to change these because the library that matches object with criteria does not support properties with dot in their names
        // TODO: This might not be needed anymore because of the new version of @azavista/advanced-search - we provide the original participant merged with the participant changes in the form
        // usedDependencies.forEach(x => {
        //     if (x.criteria) {
        //         x.criteria.forEach(criteriaOrArray => {
        //             for (const c of criteriaOrArray) {
        //                 if (c.field.id === 'Event.start') {
        //                     c.field.id = '__Event_start';
        //                 }
        //                 if (c.field.id === 'Event.end') {
        //                     c.field.id = '__Event_end';
        //                 }
        //             }
        //         });
        //     }
        // });
        const hiddenFieldIds: string[] = [];
        let mergedParticipantObject = { ...participant };
        for (const dependency of usedDependencies) {
            const fieldIdSet = new Set<string>();
            for (const criteria of dependency.criteria) {
                for (const criteriaItem of criteria) {
                    // TODO: For some reason the server changes ids by adding prefixes like Participant_
                    //       We have to remove these prefixes, because they break a lot of things (or change the server to not modify ids at all)
                    //       Assume that prefixes have only one underscore
                    //       The Event_ prefix must not be removed - original dependency has Event.start but the server
                    //       changes it to Event_start probably to use it later and get the value from the event (even though the participant has Event.start and Event.end properties already returned by the server)
                    const participantPrefix = 'Participant_';
                    const underscoreIndex = criteriaItem.field.id.indexOf(participantPrefix);
                    if (underscoreIndex >= 0) {
                        // Underscore is found - get everything after it
                        fieldIdSet.add(criteriaItem.field.id.substring(underscoreIndex + participantPrefix.length));
                    } else {
                        // No participant prefix is found
                        fieldIdSet.add(criteriaItem.field.id);
                    }
                }
            }
            const criteriaFieldIds = Array.from(fieldIdSet); // dependency.criteria.map(x => x.field.id);
            const fieldValues: { fieldId: string; value: any; }[] = [];
            for (const fieldId of criteriaFieldIds) {
                const inpEl = this.getInputElementByFieldId(fieldId);
                const field = fields.find(x => x.id === fieldId);
                if (inpEl) {
                    // Field is on the page - try to use its value
                    if (field) {
                        const value = this.utilsSvc.getInputElementValue(field, inpEl);
                        fieldValues.push({ fieldId: fieldId, value: value });
                    }
                } else {
                    // The field is not found in the DOM - try to find the value in the participant object
                    if (participant) {
                        const participantFieldValue = participant[fieldId];
                        if (participantFieldValue !== null && participantFieldValue !== undefined) {
                            fieldValues.push({ fieldId: fieldId, value: participantFieldValue });
                        } else {
                            // The participant either does not have the field or it does not have a value
                            // Try to use field's default value
                            if (field && this.hasProperty(field.schema, 'default')) {
                                fieldValues.push({ fieldId: fieldId, value: field.schema.default });
                            } else {
                                // The field does not have default value - put whatever the value is set in the participant object
                                fieldValues.push({ fieldId: fieldId, value: participantFieldValue });
                            }
                        }
                    }
                }
            }

            // const mergedParticipantObject = { ...participant };
            fieldValues.forEach(x => mergedParticipantObject[x.fieldId] = x.value);

            // Modified image and document fields must set their values in the resulting merged participant object
            const participantWithChangedImageFieldIds = this.createParticipantObjectWithChangedImageFields(userProvidedImageFieldIds, userDeletedImageFieldIds);
            mergedParticipantObject = {
                ...mergedParticipantObject,
                ...participantWithChangedImageFieldIds,
            };
            const participantWithChangedDocumentFieldIds = this.createParticipantObjectWithChangedDocumentFields(userProvidedDocumentFieldIds, userDeletedDocumentFieldIds);
            mergedParticipantObject = {
                ...mergedParticipantObject,
                ...participantWithChangedDocumentFieldIds,
            };

            // TODO: We don't need these anymore because the new @azavista/advanced-search library will get them from the participant, where they are set as Event.start and Event.end
            // mergedParticipantObject['__Event_start'] = eventStartDate;
            // mergedParticipantObject['__Event_end'] = eventEndDate;
            // const depResult = this.appSvc.getDependencyResult(dependency, mergedParticipantObject);
            // const evaluateDepResult = this.advancedSearchLibSvc.evaluateDependencies([dependency], mergedParticipantObject, event, [], [], {});
            // const depResult = !!evaluateDepResult[dependency.id];
            // result.dependencyResults.push({ dependency: dependency, result: depResult, isDependencyReferencedButMissing: false });
        }
        this.appLoggerSvc.log(LogLevel.log, '// Evaluate Dependency logs: formatted data for evaluate dependencies', {
            fields,
            dependencies: usedDependencies,
            participant: mergedParticipantObject,
            event,
            activities,
            shoppingCartResult,
            workflow,
            eventSettings,
        });
        const evaluateDepResult = this.advancedSearchLibSvc.evaluateDependencies(
            usedDependencies,
            mergedParticipantObject,
            event,
            activities,
            shoppingCartResult,
            workflow,
            eventSettings,
        );
        const evaluateDepResultKeys = Object.keys(evaluateDepResult);
        for (const key of evaluateDepResultKeys) {
            const dependency = usedDependencies.find(x => x.id === key);
            if (dependency) {
                result.dependencyResults.push({ dependency: dependency, result: evaluateDepResult[key], isDependencyReferencedButMissing: false });
            }
        }
        for (const depResult of result.dependencyResults) {
            const depHTMLElements = dependencyHTMLElements[depResult.dependency.id];
            if (depHTMLElements) {
                // The element marked as dependent exists - it could be missing if the dependency is for activity
                for (const depElement of depHTMLElements) {
                    if (!depResult.result) {
                        // Find all the field ids inside the element that is about to be hidden and add them to the result
                        hiddenFieldIds.push(...this.getFieldIdsOfParentElement(depElement));
                    }
                    this.setElementVisibility(depElement, depResult.result);
                }
            }
        }
        result.hiddenFieldIds = hiddenFieldIds;
        return result;
    }

    private createParticipantObjectWithChangedImageFields(
        userProvidedImageFieldIds: string[],
        userDeletedImageFieldIds: string[],
    ): Record<string, any> {
        const result: Record<string, any> = {};
        userProvidedImageFieldIds.forEach(x => result[x] = this.dummyObjectId);
        userDeletedImageFieldIds.forEach(x => result[x] = null);
        return result;
    }

    private createParticipantObjectWithChangedDocumentFields(
        userProvidedDocumentFieldIds: string[],
        userDeletedDocumentFieldIds: string[]
    ): Record<string, any> {
        const result: Record<string, any> = {};
        userProvidedDocumentFieldIds.forEach(x => result[x] = this.dummyObjectId);
        userDeletedDocumentFieldIds.forEach(x => result[x] = null);
        return result;
    }

    private hasProperty(obj: any, propertyName: string): boolean {
        if (!obj) {
            return false;
        }
        return !!Object.getOwnPropertyNames(obj).find(x => x === propertyName);
    }

    getFieldIdsOfParentElement(parentEl: HTMLElement): string[] {
        const fieldIds: string[] = [];
        const fieldElements = parentEl.querySelectorAll(this.getAttributeSelector(AttributeName.fieldId));
        fieldElements.forEach(x => {
            const fieldIdFromElementAtribute = x.getAttribute(AttributeName.fieldId);
            if (fieldIdFromElementAtribute) {
                fieldIds.push(fieldIdFromElementAtribute);
            }
        });
        return fieldIds;
    }

    addDynamicContentElementScript(scriptText: string): void {
        const scriptEl = document.createElement('script');
        scriptEl.type = 'text/javascript';
        scriptEl.appendChild(document.createTextNode(scriptText));
        this.dynamicEl.appendChild(scriptEl);
    }

    addDynamicContentElementStyle(cssText: string): void {
        const styleEl = document.createElement('style');
        styleEl.appendChild(document.createTextNode(cssText));
        this.dynamicEl.appendChild(styleEl);
    }

    async addPageThemeStylesheet(url: string): Promise<void> {
        let linkEl = document.head.querySelector(this.getAttributeSelector(AttributeName.azaRwsPageThemeCssLink)) as HTMLLinkElement;
        if (linkEl && linkEl.href === url) {
            // It is possible that the link that already exists is not yet loaded
            // but here we are returning resolved promise which means that the HTML
            // could be rendered before the CSS is loaded. But we do not expect calls to this function
            // while previous calls are still not completed
            return Promise.resolve();
        }
        if (linkEl) {
            // Remove the existing link which have different href
            // If not removed, just changing the href does not work well with onload event handler
            linkEl.remove();
        }
        linkEl = document.createElement('link');
        linkEl.setAttribute(AttributeName.azaRwsPageThemeCssLink, '');
        linkEl.rel = 'stylesheet';
        linkEl.type = 'text/css';
        linkEl.href = url;
        document.head.appendChild(linkEl);
        return new Promise<void>((resolve, reject) => {
            linkEl.onload = () => {
                resolve();
            };
            linkEl.onerror = () => {
                reject();
            };
        });
    }

    setDynamicContent(html: string): void {
        const dynamicEl = this.getDynamicContentContainerElement();
        dynamicEl.innerHTML = html;
        // Get all script elments and create them separately
        const scriptEls = dynamicEl.querySelectorAll('script');
        scriptEls.forEach(el => {
            dynamicEl.removeChild(el);
            this.addDynamicContentElementScript(el.text);
        });
    }

    removeElementsWithAttribute(attrName: AttributeName): void {
        const dynamicEl = this.getDynamicContentContainerElement();
        const elements = dynamicEl.querySelectorAll(this.getAttributeSelector(attrName));
        elements.forEach(x => dynamicEl.removeChild(x));
    }

    removeInputFieldsForMissingFields(fields: IField[]): void {
        const dynamicEl = this.getDynamicContentContainerElement();
        const inputFields = dynamicEl.querySelectorAll(this.getAttributeSelector(AttributeName.fieldId));
        const fieldIdsSet = new Set(fields.map(x => x.id));
        inputFields.forEach(x => {
            const fieldId = x.getAttribute(AttributeName.fieldId);
            const fieldExists = fieldIdsSet.has(fieldId);
            if (fieldExists) {
                // This field exists - do nothing
                return;
            }
            // TODO: We don't have elements with attribute aza-input-field-container but we could add this in the future
            let parent = x.closest(this.getAttributeSelector(AttributeName.azaInputFieldContainer));
            if (!parent) {
                // If not found by the unique attribute - find the div with class 'aza-input-field'
                parent = x.closest('div.' + ClassName.azaInputField);
            }
            let elementToRemove = parent || x;
            this.appLoggerSvc.log(
                LogLevel.warn,
                `Removing the following element because it references field with id ${fieldId} which was not found`,
                elementToRemove
            );
            elementToRemove.remove();
        });
    }

    setAttributeForAnchorElements() : void {
        const urlParts = window.location.href.split('#');
        const currentUrl = urlParts[0];
        const anchorElements = this.getDynamicContainerChildrenElements<HTMLAnchorElement>('a[href^="#"]');
        // need to set full URL because the page has custom base_href
        anchorElements.forEach(el => {
            const hash = el.getAttribute('href');
            el.setAttribute('href', `${currentUrl}${hash}`);
        });

        const anchorUrl = urlParts[1];
        if (anchorUrl) {
            this.getDynamicContainerChildElementById(anchorUrl)?.scrollIntoView({ behavior: 'smooth' });
        }
    }

    hideElement(el: HTMLElement): void {
        if (!el) {
            return;
        }
        el.style.display = 'none';
    }

    hideElements(elements: NodeListOf<HTMLElement>): void {
        elements.forEach(x => this.hideElement(x));
    }

    showElement(el: HTMLElement): void {
        if (!el) {
            return;
        }
        el.style.display = '';
    }

    showElements(elements: NodeListOf<HTMLElement>): void {
        elements.forEach(x => this.showElement(x));
    }

    setElementVisibility(el: HTMLElement, visible: boolean): void {
        if (visible) {
            this.showElement(el);
        } else {
            this.hideElement(el);
        }
    }

    setTranslations(translations: any, language: string, fields: IField[]): void {
        if (!translations) {
            return;
        }
        const translationLanguage = translations[language] || {};
        const translatableElements = this.getDynamicContainerChildrenElements(this.getAttributeSelector(AttributeName.translatable));
        translatableElements.forEach(el => {
            const elAttrValue = el.getAttribute(AttributeName.translatable);
            // TODO: Temporary solution - remove it once we have special section for activity list
            const hasDontTranslateAttr = el.hasAttribute('aza-dont-translate');
            if (!hasDontTranslateAttr && elAttrValue && translationLanguage[elAttrValue] !== undefined) {
                const element = el;
                element.innerHTML = translationLanguage[elAttrValue];
            }
        });

        this.setSelectFieldsTranslations(fields, language);
    }

    getAttributeSelector(attrName: AttributeName): string {
        return `[${attrName}]`;
    }

    getAttributeWithValueSelector(attrName: AttributeName, value: string): string {
        return `[${attrName}="${value}"]`;
    }

    getAttributeWithValuePrefixSelector(attrName: AttributeName, valuePrefix: string): string {
        return `[${attrName}^="${valuePrefix}"]`;
    }

    sectionButtons(translations: any, clickCallback: (ev: Event, pageId?: string) => void): void {
        const buttonElements = this.getDynamicContainerChildrenElements('section.section-button');
        buttonElements.forEach(buttonElement => {
            const linkEl = buttonElement.querySelector('a');
            if (linkEl) {
                const pageId = linkEl.getAttribute('href');
                if (pageId && pageId.match(/^[0-9a-fA-F]{24}$/)) {
                    linkEl.setAttribute('href', '');
                    linkEl.onclick = (ev) => {
                        if (clickCallback) {
                            clickCallback(ev, pageId);
                        }
                    };
                }
            }
        });
    }

    setMenuItems(menuItems: IMenuItem[], translations: any, pageData: IEventPageResponse, clickCallback: (ev: Event, pageId?: string) => void): void {
        // Find the menu parent element and create menu items with specified translation
        const menuElements = this.getDynamicContainerChildrenElements('nav.aza-navbar-items');
        if (menuElements.length === 0) {
            return;
        }
        let menus = menuItems || [];
        // Leave only menus which have page_id set to something
        menus = menus.filter(x => !!x.page_id);
        const navBarContainerElements = this.getDynamicContainerChildrenElements('div.aza-navbar');
        if (menus.length === 0) {
            // No menus provided - hide all menu containers (horizontal lines)
            this.hideElements(navBarContainerElements);
            return;
        }
        this.showElements(navBarContainerElements);
        const currentSearchParams = this.appSvc.getAllSearchParams();
        const currentSearchParamsWithoutPageId = currentSearchParams.filter(({id}) => id !== SearchParamName.page_id);
        menuElements.forEach(menuEl => {
            const logoutMenuLinkSelector = `a.${ClassName.azaLinkLogout}`;
            const logoutMenuEl = menuEl.querySelector(`li:has(${logoutMenuLinkSelector})`);
            // Remove all existing children if any
            menuEl.innerHTML = '';
            if (menus.length > 0) {
                // Create <ul>
                const ulEl = document.createElement('ul');
                for (const menuItem of menus) {
                    // Create <li> for each menu item
                    const liEl = document.createElement('li');
                    const isForCurrentPage = pageData.id === menuItem.page_id;
                    let aEl: HTMLElement;
                    if (!isForCurrentPage) {
                        // Create <a> for each <li>
                        aEl = document.createElement('a');
                        aEl.setAttribute(AttributeName.href, menuItem.href || '');
                    } else {
                        // This is element for current page - page simple span so it is not clickable
                        aEl = document.createElement('span');
                    }
                    aEl.className = 'aza-link aza-text';
                    const translationKey = menuItem.translatable;
                    // Set translation key if exists so changing translations will change the menu text too
                    if (translationKey) {
                        aEl.setAttribute(AttributeName.translatable, translationKey);
                    }
                    if (!isForCurrentPage) {
                        // Remember pageId so we can send it to the click callback
                        const menuPageId = menuItem.page_id;
                        if (menuPageId) {
                            // Published registration website needs to prefix with "event/{eventId}/"
                            const prefix = environment.production ? `event/${pageData.event?.id}/` : '';
                            const searchParams = new URLSearchParams([
                                [SearchParamName.page_id, menuPageId],
                                ...currentSearchParamsWithoutPageId.map(({id, value}) => [id, value]),
                            ]);
                            (aEl as HTMLAnchorElement).href = `${prefix}?${searchParams}`;
                        }
                        aEl.onclick = (ev) => {
                            if (clickCallback) {
                                ev.preventDefault();
                                clickCallback(ev, menuPageId);
                            }
                        };
                    }

                    // Get the translation value from the language translations
                    const translationValue = translations[translationKey];
                    if (translationValue !== undefined) {
                        // We have translation - set it as HTML (this will keep the HTML format generated in the builder)
                        aEl.innerHTML = translationValue;
                    } else {
                        // No such translation key found in translations - use default text
                        aEl.innerHTML = menuItem.label;
                    }
                    liEl.appendChild(aEl);
                    ulEl.appendChild(liEl);
                }
                if (logoutMenuEl && pageData.participant?.id) {
                    const logoutMenuLink: HTMLAnchorElement = logoutMenuEl.querySelector(logoutMenuLinkSelector);
                    if (logoutMenuLink) {
                        logoutMenuLink.href = (new URL(`w/event/${pageData.event.id}/?clear=true`, location.origin)).href;
                    }
                    ulEl.appendChild(logoutMenuEl);
                }
                menuEl.appendChild(ulEl);
            }
        });
    }

    setInputFieldsTooltipAndPlaceholder(fields: IField[], language: string, parentElement: HTMLElement = this.getDynamicContentContainerElement()): void {
        const inputAndTextareaSelector = 'div.aza-input-field input, div.aza-input-field textarea';
        const inputElements = parentElement.querySelectorAll(inputAndTextareaSelector) as NodeListOf<HTMLInputElement>;
        const selectElements = parentElement.querySelectorAll('div.aza-input-field select') as NodeListOf<HTMLSelectElement>;
        const documentElements = parentElement.querySelectorAll(`div.aza-input-field ${this.getAttributeSelector(AttributeName.azaDocumentField)}`) as NodeListOf<HTMLElement>;
        const imageElements = parentElement.querySelectorAll(`div.aza-input-field ${this.getAttributeSelector(AttributeName.azaImageField)}`) as NodeListOf<HTMLElement>;

        inputElements.forEach(inputEl => {
            const fieldId = inputEl.getAttribute(AttributeName.fieldId);
            if (fieldId) {
                const field = fields.find(x => x.id === fieldId);
                if (!field) {
                    return;
                }
                const elementTitleValue = this.utilsSvc.getFieldAttributeTranslation(
                    field,
                    FieldAttributeTranslationValue.tooltipTranslation,
                    language,
                    field.tool_tip
                );
                this.setTooltipToInputFieldLabelElement(inputEl, elementTitleValue);
                if (field) {
                    this.setInputElementInfoIconOrElementItselfTooltipByParent(inputEl, elementTitleValue);
                    inputEl.placeholder = this.utilsSvc.getFieldAttributeTranslation(
                        field,
                        FieldAttributeTranslationValue.placeholderTranslation,
                        language,
                        field.placeholder
                    );
                }
                // Check if this input element is sibling of a label element which means this is a checkbox
                const siblingLabelElement: HTMLLabelElement = inputEl.parentElement.querySelector(
                    this.getAttributeWithValueSelector(AttributeName.for, inputEl.id)
                );
                if (siblingLabelElement) {
                    siblingLabelElement.title = elementTitleValue;
                }
                // Check if this input element is sibling of a div for opinion scale items
                const siblingOpinionScaleElement: HTMLDivElement = inputEl.parentElement.querySelector(
                    this.getAttributeSelector(AttributeName.azaOpinionScaleFieldValuesContainer)
                );
                if (siblingOpinionScaleElement) {
                    siblingOpinionScaleElement.title = elementTitleValue;
                }
                // Check if this input element is sibling of a div for rating items
                const siblingRatingElement: HTMLDivElement = inputEl.parentElement.querySelector(
                    this.getAttributeSelector(AttributeName.azaRatingFieldValuesContainer)
                );
                if (siblingRatingElement) {
                    siblingRatingElement.title = elementTitleValue;
                }
            }
        });

        selectElements.forEach(selectEl => {
            const fieldId = selectEl.getAttribute(AttributeName.fieldId);
            if (fieldId) {
                const field = fields.find(x => x.id === fieldId);
                if (field) {
                    const elementTitleValue = this.utilsSvc.getFieldAttributeTranslation(
                        field,
                        FieldAttributeTranslationValue.tooltipTranslation,
                        language,
                        field.tool_tip
                    );
                    this.setTooltipToInputFieldLabelElement(selectEl, elementTitleValue);
                    this.setInputElementInfoIconOrElementItselfTooltipByParent(selectEl, elementTitleValue);
                }
            }
        });

        this.setDocumentOrImageFieldsTooltip(documentElements, fields, language);
        this.setDocumentOrImageFieldsTooltip(imageElements, fields, language);
    }

    setDocumentOrImageFieldsTooltip(elements: NodeListOf<HTMLElement>, fields: IField[], language: string): void {
        elements.forEach(el => {
            const fieldId = el.getAttribute(AttributeName.fieldId);
            if (fieldId) {
                const field = fields.find(x => x.id === fieldId);
                if (!field) {
                    return;
                }
                const elementTitleValue = this.utilsSvc.getFieldAttributeTranslation(
                    field,
                    FieldAttributeTranslationValue.tooltipTranslation,
                    language,
                    field.tool_tip
                );
                let elementToSetTitleTo: HTMLElement;
                const iconEl: HTMLElement = el.querySelector(this.getAttributeSelector(AttributeName.azaInputFieldLabelInfoIcon));
                if (!iconEl) {
                    elementToSetTitleTo = el.querySelector('label');
                }
                elementToSetTitleTo ||= el;
                this.setTooltipToInputFieldLabelElement(elementToSetTitleTo, elementTitleValue);
                this.setTooltipToIconElementOrOtherElement(elementToSetTitleTo, iconEl, elementTitleValue);
            }
        });
    }

    setInputElementInfoIconOrElementItselfTooltipByParent(el: HTMLElement, tooltip: string): void {
        // Try to find "info" icon and set title to it. Otherwise set the title to the element itself
        let iconEl: HTMLElement;
        const elementParent = this.getInputFieldParent(el);
        if (elementParent) {
            // Parent element is found, try to find label container element
            const labelContainerEl = elementParent.querySelector(this.getAttributeSelector(AttributeName.azaInputFieldLabelContainer));
            if (labelContainerEl) {
                iconEl = labelContainerEl.querySelector(this.getAttributeSelector(AttributeName.azaInputFieldLabelInfoIcon));
            }
        } else {
            el.title = tooltip;
        }
        const elementToSetTitleTo = iconEl || el;
        this.setTooltipToIconElementOrOtherElement(elementToSetTitleTo, iconEl, tooltip);
    }

    setTooltipToIconElementOrOtherElement(otherElement: HTMLElement, iconEl?: HTMLElement, tooltip?: string): void {
        if (iconEl) {
            const hasTitle = !!tooltip?.trim();
            if (hasTitle) {
                iconEl.title = tooltip;
                this.showElementWithCss(iconEl);
            } else {
                this.hideElementWithCss(iconEl);
            }
        } else {
            otherElement.title = tooltip;
            this.showElementWithCss(otherElement);
        }
    }

    setTooltipToInputFieldLabelElement(inputFieldEl: HTMLElement, tooltip: string): void {
        let labelEl = inputFieldEl.parentElement.querySelector('label');
        if (!labelEl) {
            // The label is not a sibling to the input element
            // select, date/date-time/time and rating/opinion fields are having label in the parent of the parent
            labelEl = inputFieldEl.parentElement.parentElement.querySelector('label');
        }
        if (labelEl) {
            labelEl.title = tooltip;
        }
    }

    createJsonFromFormFields(
        fields: IField[], excludeFieldIds: string[], eventTimezone: string, formEl?: HTMLElement
    ): Record<string, any> {
        if (excludeFieldIds && excludeFieldIds.length > 0) {
            fields = fields.filter(x => excludeFieldIds.indexOf(x.id) === -1);
        }
        const result: Record<string, any> = {};
        formEl ||= this.getDynamicContentContainerElement();
        const nonMultiRegFormSelector = formEl === this.getDynamicContentContainerElement() ? ':not(.section-multi-registration-form *) ' : '';
        const inputAndTextareaSelector = `div.aza-input-field input${nonMultiRegFormSelector}, div.aza-input-field textarea${nonMultiRegFormSelector}`;
        const inputElements = formEl.querySelectorAll(inputAndTextareaSelector) as NodeListOf<HTMLInputElement>;
        const selectElements = formEl.querySelectorAll(`div.aza-input-field select${nonMultiRegFormSelector}`) as NodeListOf<HTMLSelectElement>;

        inputElements.forEach(inputEl => {
            const fieldId = inputEl.getAttribute(AttributeName.fieldId);
            const field = this.getFieldFromInputElementId(fieldId, fields);
            if (field) {
                let value = this.utilsSvc.getInputElementValue(field, inputEl);
                const fieldType = this.utilsSvc.getFieldTypeFromField(field);
                if (fieldType === 'datetime') {
                    const valueWithoutTimeZone = (value as string)?.substring(0, 19);
                    if (valueWithoutTimeZone) {
                        value = this.momentSvc.getDateTimeWithTimezone(valueWithoutTimeZone, eventTimezone);
                    }
                } else if (fieldType === 'travel-date') {
                    const valueWithoutTimeZone = (value as string)?.substring(0, 19);
                    if (valueWithoutTimeZone) {
                        value = valueWithoutTimeZone + 'Z';
                    }
                }
                result[field.id] = value;
            }
        });

        selectElements.forEach(selectEl => {
            const fieldId = selectEl.getAttribute(AttributeName.fieldId);

            const field = this.getFieldFromInputElementId(fieldId, fields);
            if (field) {
                if (selectEl.multiple) {
                    result[field.id] = this.getSelectOptionsAsArray(selectEl);
                    if (result[field.id].length === 1 &&
                        result[field.id][0] === '') {
                        result[field.id] = [];
                    }
                } else if (selectEl.getAttributeNames().indexOf(AttributeName.timeHoursSelect) >= 0) {
                    // Select element for time field - get combined value from it and its sibling for minutes
                    result[field.id] = this.utilsSvc.getInputElementValue(field, selectEl);
                } else {
                    result[field.id] = selectEl.value;
                }
            }
        });
        return result;
    }

    getFieldFromInputElementId(inputFieldId: string, fields: IField[]): IField | undefined {
        const field = fields.find(x => x.id === inputFieldId);
        return field;
    }

    setElementHref(element: HTMLElement, value: string): void {
        const anchorEl = element as HTMLAnchorElement;
        anchorEl.hidden = !value;
        anchorEl.href = value || '#';
    }

    setUpdateUrlLinkCallbackForInputElement(element: HTMLElement, anchorElements: HTMLElement[]): void {
        const inpEl = element as HTMLInputElement;
        inpEl.addEventListener('keyup', () => anchorElements.forEach(el => this.setElementHref(el, inpEl.value)));
    }

    getDateValueInEventTimeZone(dateTimeValue: string): string {
        const dateTimeToEventTimeZone = this.getDateTimeValueInEventTimeZone(dateTimeValue);
        return dateTimeToEventTimeZone.substring(0, 10);
    }

    getTimeValueInEventTimeZone(dateTimeValue: string): string {
        const dateTimeToEventTimeZone = this.getDateTimeValueInEventTimeZone(dateTimeValue);
        return dateTimeToEventTimeZone.substring(11, 16);
    }

    getDateTimeValueInEventTimeZone(dateTimeValue: string): string {
        const dateTimeToEventTimeZone = this.momentSvc.getDateTimeWithTimezone(dateTimeValue, this.eventSettings.timezone, 'YYYY-MM-DD HH:mm:ss');
        return dateTimeToEventTimeZone;
    }

    setFormInputElementValue(field: IField, inputElement: HTMLElement, value: any, parentElement: HTMLElement = this.getDynamicContentContainerElement()): void {
        const inpEl = inputElement as HTMLInputElement;
        const type = inpEl.type.toLowerCase();
        if (type === 'select-multiple') {
            const valueArr = (value || []) as string[];
            const selectEl = inpEl as any as HTMLSelectElement;
            for (let i = 0; i < selectEl.options.length; i++) {
                const option = selectEl.options.item(i);
                option.selected = valueArr.indexOf(option.value) >= 0;
                const checkboxInput = parentElement.querySelector(`#${getCheckboxId(field, option.value, true)}`) as HTMLInputElement;
                if (checkboxInput) {
                    checkboxInput.checked = option.selected;
                }
            }
        } else if (type === 'select-one') {
            const isTime = inputElement.getAttributeNames().indexOf(AttributeName.timeHoursSelect) >= 0;
            if (isTime) {
                // This is the hours select of time type field - set values to it and its sibling for minutes
                this.setTimeFieldValue(inpEl, value);
            } else {
                // This is normal select
                inpEl.value = value;
                const radioButtonInput = parentElement.querySelector(`#${getRadioId(field, value, true)}`) as HTMLInputElement;
                if (radioButtonInput && !radioButtonInput.checked) {
                    radioButtonInput.checked = true;
                }
            }
        } else if (type === 'checkbox') {
            inpEl.checked = value;
        } else if (type === 'date') {
            // TODO: We have to transform the date to event time zone if the
            let dateValue: string;
            if (value) {
                if (field.schema.format === 'date-time') {
                    if (field.type === 'datetime') {
                        dateValue = this.getDateValueInEventTimeZone(value);
                    } else if (field.type === 'travel-date') {
                        // Get just the date part so we don't convert the date to event time zone (which can change the date if the time is close to 00:00)
                        dateValue = value ? (value as string).substring(0, 10) : '';
                    }
                } else {
                    dateValue = value ? (value as string).substring(0, 10) : '';
                }
            } else {
                dateValue = '';
            }
            inpEl.value = dateValue;
            // const isDateTime = element.getAttributeNames().indexOf(AttributeName.dateTimeInput) >= 0;
            // if (isDateTime) {
            //     // This is date-time field - input field is of type date but with additional drop-downs for hour and minute
            //     this.setDateTimeFieldValue(inpEl, value);
            // } else {
            //     // Just a date
            //     inpEl.value = value;
            // }
        } else if (type === 'time') {
            const valueString = value as string;
            let timeValue: string;
            if (!valueString) {
                timeValue = '';
            } else if (valueString.length === 8) {
                // Th value is in format HH:mm:ss - we need to remove seconds or ajv validation will complain
                timeValue = valueString.substring(0, 5);
            } else if (valueString.length > 8) {
                // Probably full ISO date in format YYYY-MM-DDTHH:mm:ss...
                if (field.type === 'travel-date') {
                    // This is a travel date field - get the time without modification
                    timeValue = valueString.substring(11, 16);
                } else {
                    // This is normal datetime field - convert to event time zone and get only the time part
                    timeValue = this.getTimeValueInEventTimeZone(valueString);
                }
            } else {
                timeValue = valueString;
            }
            inpEl.value = timeValue;
        } else if (type === 'text') {
            inpEl.value = value || '';
        } else if (type === 'number') {
            inpEl.value = value || '';
        } else {
            // TODO: Support all possible input elements/widgets
            inpEl.value = value;
        }
    }

    setDateTimeFieldValue(inpEl: HTMLInputElement, value: string): void {
        if (!value) {
            return;
        }
        const date = value.substr(0, 10);
        inpEl.value = date;
        const hours = +value.substr(11, 2);
        const minutes = +value.substr(14, 2);
        const parent = inpEl.parentElement;
        const hoursEl = parent.querySelector(this.getAttributeSelector(AttributeName.dateTimeHoursSelect)) as HTMLSelectElement;
        hoursEl.selectedIndex = hours;
        const minutesEl = parent.querySelector(this.getAttributeSelector(AttributeName.dateTimeMinutesSelect)) as HTMLSelectElement;
        minutesEl.selectedIndex = minutes + 4;
    }

    setTimeFieldValue(inpEl: HTMLInputElement, value: string): void {
        if (!value) {
            return;
        }
        const hours = +value.substr(0, 2);
        const minutes = +value.substr(3, 2);
        const parent = inpEl.parentElement;
        const hoursEl = parent.querySelector(this.getAttributeSelector(AttributeName.timeHoursSelect)) as HTMLSelectElement;
        hoursEl.selectedIndex = hours;
        const minutesEl = parent.querySelector(this.getAttributeSelector(AttributeName.timeMinutesSelect)) as HTMLSelectElement;
        minutesEl.selectedIndex = minutes + 4;
    }

    // getFormInputElementValue(element: HTMLElement): string | number | boolean | any {
    //     const inpEl = element as HTMLInputElement;
    //     const type = inpEl.type.toLowerCase();
    //     if (type === 'text') {
    //         return inpEl.value;
    //     } else if (type === 'date') {
    //         return inpEl.value; // AsDate.iso.toISOString();
    //     } else if (type === 'number') {
    //         return inpEl.valueAsNumber;
    //     } else if (type === 'checkbox') {
    //         return inpEl.checked;
    //     } else if (type === 'select-multiple') {
    //         const multiSelectEl = inpEl as any as HTMLSelectElement;
    //         const selectedOptionValues: string[] = [];
    //         for (let i = 0; i < multiSelectEl.selectedOptions.length; i++) {
    //             selectedOptionValues.push(multiSelectEl.selectedOptions.item(i).value);
    //         }
    //         return selectedOptionValues;
    //     } else {
    //         // TODO: Support the custom date-time
    //         return inpEl.value;
    //     }
    // }

    getSelectOptionsAsArray(selectEl: HTMLSelectElement): string[] {
        const result: string[] = [];
        const optionElements = selectEl.selectedOptions;
        for (let i = 0; i < optionElements.length; i++) {
            result.push(optionElements.item(i).value);
        }
        return result;
    }

    getInputFieldParent(childEl: HTMLElement): HTMLElement {
        let parent = childEl.parentElement;
        while (parent) {
            if (!parent) {
                return null;
            }
            if (this.containsClass(parent, ClassName.azaInputField)) {
                return parent;
            }
            parent = parent.parentElement;
        }
        return null;
    }

    getChildElementByIdPrefix(parentEl: HTMLElement, childElementIdPrefix: string): HTMLElement {
        return parentEl.querySelector(this.getAttributeWithValuePrefixSelector(AttributeName.id, childElementIdPrefix));
    }

    getChildElementById(parentEl: HTMLElement, childElementId: string): HTMLElement {
        return parentEl.querySelector(this.getAttributeWithValueSelector(AttributeName.id, childElementId));
    }

    getChildElementByAttribute(parentEl: HTMLElement, childElementAttributeName: string): HTMLElement {
        return parentEl.querySelector(this.getAttributeSelector(childElementAttributeName as AttributeName));
    }

    getChildElementBySelector(parentEl: HTMLElement, selector: string): HTMLElement {
        return parentEl.querySelector(selector);
    }

    getDynamicContainerChildElementById(childElementId: string): HTMLElement {
        return this.dynamicEl.querySelector(this.getAttributeWithValueSelector(AttributeName.id, childElementId));
    }

    getDynamicContainerChildElement(selector: string): HTMLElement {
        return this.dynamicEl.querySelector(selector);
    }

    getDynamicContainerChildrenElements<T extends HTMLElement>(selector: string): NodeListOf<T> {
        return this.getDynamicContentContainerElement().querySelectorAll(selector);
    }

    getDynamicContentContainerElement(): HTMLDivElement {
        if (this.dynamicEl) {
            return this.dynamicEl;
        }
        this.dynamicEl = document.querySelector(this.getAttributeSelector(AttributeName.azaDynamicContentContainer)) as HTMLDivElement;
        return this.dynamicEl;
    }

    createYouTubeIframeAPIReadyCallback(): void {
        const wnd = window as any;
        if (!wnd.onYouTubeIframeAPIReady) {
            wnd.onYouTubeIframeAPIReady = () => {
                this.youtubeIframeAPIReady = true;
                this.youtubeIframeAPIReadySubject.next();
            };
        }
    }

    setValidationResultToInputElement(
        fieldId: string,
        inputElement: HTMLElement,
        validationResult: IValidationResult,
        allFieldsToFindByFieldId: IField[],
        errorsContainerElementId: string,
        inputFieldParentEl?: HTMLElement
    ): void {
        if (!validationResult) {
            return;
        }
        inputFieldParentEl ||= this.getInputFieldParent(inputElement);
        if (!inputFieldParentEl) {
            return;
        }
        if (errorsContainerElementId) {
            const errorsEl = this.getChildElementByIdPrefix(inputFieldParentEl, errorsContainerElementId);
            // if (!errorsEl) {
            //     return;
            // }
            if (errorsEl) {
                if (validationResult.isValid) {
                    // Valid - hide error container element if exists
                    this.hideElement(errorsEl);
                } else {
                    // Invalid - set error container element text to validationResult.errorsText and show it
                    if (validationResult.isRequiredButNotProvided) {
                        // The field is required but value was not provided
                        // errorsEl.innerText = 'This field is required';
                    } else {
                        // Validation error
                        const field = allFieldsToFindByFieldId.find(x => x.id === fieldId);
                        let translatedErrorMessage: string;
                        if (field) {
                            translatedErrorMessage = this.utilsSvc.getFieldAttributeTranslation(
                                field,
                                FieldAttributeTranslationValue.errorMessageTranslation,
                                this.translateSvc.currentLang,
                                field.error_message || ''
                            );
                        } else {
                            translatedErrorMessage = validationResult.errorsText;
                        }
                        errorsEl.innerText = translatedErrorMessage || validationResult.errorsText || '';
                    }
                    this.showElement(errorsEl);
                }
            }
        }
    }

    showElementWithCss(el: Element): void {
        el?.classList.remove('display-none');
    }

    hideElementWithCss(el: Element): void {
        el?.classList.add('display-none');
    }

    changeElementVisibilityWithCss(el: Element, visible: boolean): void {
        if (visible) {
            this.showElementWithCss(el);
        } else {
            this.hideElementWithCss(el);
        }
    }
}

function simulateClickEvent(element: HTMLElement) {
    element.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true, cancelable: true }));
    element.dispatchEvent(new MouseEvent('mouseover', { bubbles: true, cancelable: true }));
    element.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
    element.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    element.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true }));
    element.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true, cancelable: true }));
}

/**
 *
 * @param field
 * @param itemValue
 * @param isEscaped set to true if its to be used as query-selector
 * @returns
 */
function getCheckboxId(field: IField, itemValue: any, isEscaped = false): string {
    const id = `checkbox_${field.id}_${itemValue}`;
    return isEscaped ? CSS.escape(id) : id;
}

/**
 *
 * @param field
 * @param itemValue
 * @param isEscaped set to true if its to be used as query-selector
 * @returns
 */
function getRadioId(field: IField, itemValue: any, isEscaped = false): string {
    const id = `radio_${field.id}_${itemValue}`;
    return isEscaped ? CSS.escape(id) : id;
}

